import React, { useState, useEffect, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sidebar,
  MainContent,
  ContentCard,
  StatCard,
  Icons
} from './ModernSidebarLayout';
import { toEasternInputDate } from '../dateHelper';
import {
  subscribePreparer as subscribeToNotifications,
  unsubscribePreparer as unsubscribeFromNotifications,
  isPreparerSubscribed,
  setupNotificationListener,
  isPushSupported,
  getNotificationPermission,
  isVapidKeyConfigured,
  checkAndSendReminders
} from '../pushNotificationService';
import {
  subscribeToAvailability,
  subscribeToAppointments,
  updateAppointment,
  addAppointment,
  deleteAppointment,
  setAvailability as saveAvailability,
  updatePreparer,
  // eslint-disable-next-line no-unused-vars
  createConsentRequest,
  subscribeToConsentRequests,
  subscribeToSignedConsents,
  deleteConsentRequest,
  // eslint-disable-next-line no-unused-vars
  saveDocumentTemplate,
  subscribeToDocumentTemplates,
  // eslint-disable-next-line no-unused-vars
  createDocumentRequest,
  subscribeToDocumentRequests,
  subscribeToSignedDocuments,
  // eslint-disable-next-line no-unused-vars
  deleteDocumentTemplate,
  // eslint-disable-next-line no-unused-vars
  deleteDocumentRequest,
  submitSignedDocument,
  addTaxReturn,
  updateTaxReturn,
  deleteTaxReturn,
  subscribeToTaxReturns,
  subscribeToIRSWaiverSubmissions,
  verifyIRSWaiverSubmission,
  deleteIRSWaiverSubmission,
  // eslint-disable-next-line no-unused-vars
  completeIRSWaiverSubmission
} from '../firestoreHelper';
import { sendConsentEmail, sendReminderEmail, sendTaxSeasonEmail, sendConfirmationEmail } from '../emailService';
import { downloadIRSWaiverPDF, generateIRSWaiverPDF } from '../pdfGenerator';
// eslint-disable-next-line no-unused-vars
import PDFDocumentBuilder from './PDFDocumentBuilder';
// VideoConference opens in new tab

// Set worker source for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Menu items for Preparer Dashboard
const getPreparerMenuItems = (counts = {}, isAdmin = false) => [
  {
    id: 'appointments',
    label: 'Appointments',
    icon: Icons.Calendar,
    badge: counts.appointments || 0
  },
  {
    id: 'today',
    label: 'Today',
    icon: Icons.Clock,
    badge: counts.today || 0
  },
  {
    id: 'availability',
    label: 'Availability',
    icon: Icons.Calendar
  },
  {
    id: 'divider-1',
    type: 'divider',
    label: 'Documents'
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: Icons.Document,
    badge: counts.documents || 0
  },
  {
    id: 'taxreturns',
    label: 'Tax Returns',
    icon: Icons.DollarSign,
    badge: counts.taxReturns || 0
  },
  {
    id: 'divider-2',
    type: 'divider',
    label: 'Actions'
  },
  {
    id: 'new-booking',
    label: 'New Booking',
    icon: Icons.Plus
  },
  {
    id: 'profile',
    label: 'Edit Profile',
    icon: Icons.Settings
  }
];

function PreparerDashboard({ preparer, company, onLogout, onUpdatePreparer, onSwitchToAdmin }) {
  // Persist active tab across page refreshes
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('preparerActiveTab');
    return savedTab || 'appointments';
  });
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Mobile responsive states
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 600;
  const isExtraSmallMobile = windowWidth < 480;
  const isTablet = windowWidth < 900;
  
  // Modern sidebar states
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Calendar-based availability states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

  // Helper to format timeSlots - handles both old format (string[]) and new format ({date,time}[])
  const formatTimeSlots = (apt) => {
    if (!apt.timeSlots || !Array.isArray(apt.timeSlots) || apt.timeSlots.length === 0) {
      return apt.time ? convert24to12Hour(apt.time) : 'N/A';
    }
    // New format: array of {date, time} objects
    if (typeof apt.timeSlots[0] === 'object' && apt.timeSlots[0].time) {
      const uniqueDates = [...new Set(apt.timeSlots.map(s => s.date))];
      if (uniqueDates.length === 1) {
        // All same date - just show times
        return apt.timeSlots.map(s => convert24to12Hour(s.time)).join(', ');
      } else {
        // Different dates - show date + time for each
        return apt.timeSlots.map(s => `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric'})} ${convert24to12Hour(s.time)}`).join(', ');
      }
    }
    // Old format: array of time strings
    return apt.timeSlots.map(t => convert24to12Hour(t)).join(', ');
  };

  const getFirstTime = (apt) => {
    if (apt.timeSlots && Array.isArray(apt.timeSlots) && apt.timeSlots.length > 0) {
      if (typeof apt.timeSlots[0] === 'object') return apt.timeSlots[0].time || '00:00';
      return typeof apt.timeSlots[0] === 'string' ? apt.timeSlots[0] : '00:00';
    }
    return typeof apt.time === 'string' ? apt.time : '00:00';
  };

  // Edit Profile states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    photo: '',
    bio: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Push notification states
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Check if running as PWA
  const isPWAMode = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone === true
  );
  
  // Photo cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Consent document states
  const [consentRequests, setConsentRequests] = useState([]);
  const [signedConsents, setSignedConsents] = useState([]);
  const [showSendConsentModal, setShowSendConsentModal] = useState(false);
  const [sendingConsent, setSendingConsent] = useState(false);
  const [viewingConsent, setViewingConsent] = useState(null);
  const [consentForm, setConsentForm] = useState({
    taxpayerName: '',
    taxpayerEmail: '',
    taxpayerPhone: '',
    taxYear: new Date().getFullYear() - 1
  });

  // PDF Document Template states (DocuSign-like) - kept for future use
  // eslint-disable-next-line no-unused-vars
  const [documentTemplates, setDocumentTemplates] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [documentRequests, setDocumentRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [signedDocuments, setSignedDocuments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showDocumentBuilder, setShowDocumentBuilder] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedTemplateSubmissions, setExpandedTemplateSubmissions] = useState({});
  const [editingPendingRequest, setEditingPendingRequest] = useState(null);
  const [pendingFormValues, setPendingFormValues] = useState({});
  const [savingPendingRequest, setSavingPendingRequest] = useState(false);
  const [viewingCompletedDoc, setViewingCompletedDoc] = useState(null);
  const [viewerNumPages, setViewerNumPages] = useState(null);
  const [viewerCurrentPage, setViewerCurrentPage] = useState(1);
  const [viewerScale, setViewerScale] = useState(1.0);
  const [showSendDocumentModal, setShowSendDocumentModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingDocument, setSendingDocument] = useState(false);
  const [documentSendForm, setDocumentSendForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: ''
  });

  // Search and filter states for Completed Forms
  const [completedSearchName, setCompletedSearchName] = useState('');
  const [completedSearchDateFrom, setCompletedSearchDateFrom] = useState('');
  const [completedSearchDateTo, setCompletedSearchDateTo] = useState('');
  const [selectedCompletedForms, setSelectedCompletedForms] = useState([]);

  // IRS Waiver Submissions states
  const [irsWaiverSubmissions, setIrsWaiverSubmissions] = useState([]);
  const [viewingWaiverSubmission, setViewingWaiverSubmission] = useState(null);
  const [selectedPendingForms, setSelectedPendingForms] = useState([]);

  // Selected appointments for bulk actions
  const [selectedAppointments, setSelectedAppointments] = useState([]);

  // Pagination states for appointments
  const [pendingPage, setPendingPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [todayPage, setTodayPage] = useState(1);
  const appointmentsPerPage = 10;

  // Tax Returns states
  const [taxReturns, setTaxReturns] = useState([]);
  const [taxReturnForm, setTaxReturnForm] = useState({
    clientName: '',
    phone: '',
    amount: '',
    taxYear: new Date().getFullYear() - 1,
    paymentStatus: 'unpaid'
  });
  const [savingTaxReturn, setSavingTaxReturn] = useState(false);
  const [editingTaxReturn, setEditingTaxReturn] = useState(null);

  // Date filter for appointments
  const [filterDate, setFilterDate] = useState('');

  // Past Appointments visibility toggle
  const [showPastAppointments, setShowPastAppointments] = useState(false);

  // View Appointment Details modal state
  const [expandedAppointmentId, setExpandedAppointmentId] = useState(null);

  // Multi-date selection for availability
  const [selectedDates, setSelectedDates] = useState([]);

  // Track edited availability (pending changes to existing slots)
  const [editedAvailability, setEditedAvailability] = useState({});

  // New Booking form states
  const [newBookingForm, setNewBookingForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    numberOfClients: '1',
    fillingType: '',
    date: '',
    time: '',
    notes: ''
  });
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  
  // Edit appointment states
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [originalEmail, setOriginalEmail] = useState('');

  // Generate time slots from 6 AM to 11 PM in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 23 && minute === 30) break; // Stop at 11:00 PM
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Window resize listener for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check notification subscription status on mount
  useEffect(() => {
    const checkNotifications = async () => {
      if (!preparer?.id || !company?.id) return;
      
      // Check if push notifications are supported
      if (!isPushSupported()) {
        setNotificationPermission('unsupported');
        return;
      }
      
      // Check if VAPID key is configured
      if (!isVapidKeyConfigured()) {
        console.log('VAPID key not configured, skipping notification check');
        return;
      }
      
      // Get current permission status
      const permission = getNotificationPermission();
      setNotificationPermission(permission);
      
      // Check if preparer is subscribed
      const subscribed = await isPreparerSubscribed(preparer.id, company.id);
      setNotificationsEnabled(subscribed && permission === 'granted');
      
      // If PWA mode and notifications not enabled, show prompt after a short delay
      if (isPWAMode && permission === 'default' && !subscribed) {
        // Check if user has dismissed prompt before (within last 24 hours)
        const lastDismissed = localStorage.getItem('notificationPromptDismissed');
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        
        if (!lastDismissed || parseInt(lastDismissed) < oneDayAgo) {
          setTimeout(() => {
            setShowNotificationPrompt(true);
          }, 2000); // Show after 2 seconds
        }
      }
      
      // If PWA mode and permission granted but not subscribed, auto-subscribe
      if (isPWAMode && permission === 'granted' && !subscribed) {
        console.log('📱 PWA mode: Auto-subscribing to notifications...');
        const result = await subscribeToNotifications(preparer.id, company.id);
        if (result.success) {
          setNotificationsEnabled(true);
          console.log('✅ Auto-subscribed to notifications');
        }
      }
    };
    
    checkNotifications();
  }, [preparer?.id, company?.id, isPWAMode]);

  // Setup foreground notification listener
  useEffect(() => {
    if (!notificationsEnabled) return;
    
    const unsubscribe = setupNotificationListener((payload) => {
      console.log('📬 New notification received:', payload);
      // You could update state here to show in-app notification
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [notificationsEnabled]);

  // Listen for notification click messages from service worker
  useEffect(() => {
    const handleServiceWorkerMessage = async (event) => {
      console.log('📩 Service Worker message received:', event.data);
      
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        const { data, action } = event.data;
        const appointmentId = data?.appointmentId;
        
        if (!appointmentId) return;
        
        // Handle confirm action
        if (action === 'confirm') {
          try {
            const apt = appointments.find(a => a.id === appointmentId);
            if (apt && apt.status !== 'confirmed') {
              await updateAppointment(appointmentId, { 
                status: 'confirmed', 
                confirmed: true,
                confirmedAt: new Date().toISOString()
              });
              alert('✅ Appointment confirmed!');
            }
          } catch (err) {
            console.error('Error confirming appointment:', err);
            alert('❌ Could not confirm appointment');
          }
        }
        
        // Switch to today's tab and highlight appointment
        setActiveTab('today');
        
        // Scroll to appointment if visible
        setTimeout(() => {
          const element = document.getElementById(`appointment-${appointmentId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.boxShadow = '0 0 0 3px #3b82f6';
            setTimeout(() => {
              element.style.boxShadow = '';
            }, 3000);
          }
        }, 500);
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [appointments]);

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('preparerActiveTab', activeTab);
  }, [activeTab]);

  // DEBUG: Fetch all appointments to check what's in database
  useEffect(() => {
    if (!preparer?.id) return;
    
    // Debug function to check all appointments in Firestore
    const debugFetchAllAppointments = async () => {
      try {
        const { getDocs, collection } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const snapshot = await getDocs(collection(db, 'appointments'));
        console.log('🗄️ DEBUG - ALL appointments in Firestore:');
        snapshot.forEach(doc => {
          const data = doc.data();
          console.log(`  ID: ${doc.id}`, {
            client: data.fullName || data.clientName,
            preparer: data.preparer,
            preparerId: data.preparerId,
            date: data.date,
            status: data.status,
            companyId: data.companyId
          });
        });
        console.log(`🗄️ Total: ${snapshot.size} appointments in database`);
      } catch (err) {
        console.error('Debug fetch error:', err);
      }
    };
    
    debugFetchAllAppointments();
  }, [preparer?.id]);

  // Subscribe to appointments for this preparer
  useEffect(() => {
    if (!preparer?.id) return;
    
    console.log('🔍 Preparer Dashboard - Subscribing to appointments for:', {
      preparerId: preparer.id,
      preparerName: preparer.name
    });

    const unsubscribeAppointments = subscribeToAppointments((allAppointments) => {
      console.log('📥 Received', allAppointments.length, 'total appointments from Firestore');
      
      // Filter appointments for this preparer (check both id and name for compatibility)
      const myAppointments = allAppointments.filter(apt => 
        apt.preparerId === preparer.id || 
        apt.preparer === preparer.name ||
        apt.preparerName === preparer.name
      );
      
      console.log('👤 Found', myAppointments.length, 'appointments for this preparer');
      if (myAppointments.length > 0) {
        console.log('📋 Sample appointment:', myAppointments[0]);
      }
      
      setAppointments(myAppointments);
      setLoading(false);
    });

    return () => unsubscribeAppointments();
  }, [preparer?.id, preparer?.name]);

  // Subscribe to availability for this preparer
  useEffect(() => {
    if (!preparer?.id) return;

    const unsubscribeAvailability = subscribeToAvailability((allAvailability) => {
      // Filter availability for this preparer
      const myAvailability = {};
      Object.keys(allAvailability).forEach(key => {
        if (key.startsWith(preparer.id + '_') || key.startsWith(preparer.name + '_')) {
          myAvailability[key] = allAvailability[key];
        }
      });
      setAvailability(myAvailability);
    });

    return () => unsubscribeAvailability();
  }, [preparer?.id, preparer?.name]);

  // Update selected time slots when date changes
  useEffect(() => {
    if (selectedDate && preparer) {
      const dateStr = formatDateToString(selectedDate);
      const key = `${preparer.id}_${dateStr}`;
      const altKey = `${preparer.name}_${dateStr}`;
      const savedSlots = availability[key] || availability[altKey] || [];
      setSelectedTimeSlots(savedSlots);
    }
  }, [selectedDate, availability, preparer]);

  // Subscribe to consent requests and signed consents
  useEffect(() => {
    if (!preparer?.id) return;

    const unsubscribeRequests = subscribeToConsentRequests(preparer.id, (requests) => {
      setConsentRequests(requests);
    });

    const unsubscribeConsents = subscribeToSignedConsents(preparer.id, (consents) => {
      setSignedConsents(consents);
    });

    return () => {
      unsubscribeRequests();
      unsubscribeConsents();
    };
  }, [preparer?.id]);

  // Subscribe to document templates and signed documents
  useEffect(() => {
    if (!preparer?.id) return;

    const unsubscribeTemplates = subscribeToDocumentTemplates(preparer.id, (templates) => {
      setDocumentTemplates(templates);
    });

    const unsubscribeDocRequests = subscribeToDocumentRequests(preparer.id, (requests) => {
      setDocumentRequests(requests);
    });

    const unsubscribeSignedDocs = subscribeToSignedDocuments(preparer.id, (docs) => {
      setSignedDocuments(docs);
    });

    return () => {
      unsubscribeTemplates();
      unsubscribeDocRequests();
      unsubscribeSignedDocs();
    };
  }, [preparer?.id]);

  // Subscribe to tax returns
  useEffect(() => {
    if (!preparer?.id) return;

    const unsubscribeTaxReturns = subscribeToTaxReturns(preparer.id, (returns) => {
      setTaxReturns(returns);
    });

    return () => unsubscribeTaxReturns();
  }, [preparer?.id]);

  // Subscribe to IRS Waiver submissions
  useEffect(() => {
    if (!preparer?.id) return;

    const unsubscribeWaivers = subscribeToIRSWaiverSubmissions(
      preparer.id, 
      false, // not admin view
      (submissions) => {
        setIrsWaiverSubmissions(submissions);
      },
      preparer.companyId || null
    );

    return () => unsubscribeWaivers();
  }, [preparer?.id, preparer?.companyId]);

  // Auto-reminder: Send reminders 15 minutes before appointments
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sentAutoReminders = useRef(new Set());
  
  useEffect(() => {
    if (!preparer?.id || !appointments.length) return;

    const checkAndSendReminders = async () => {
      const now = new Date();
      
      for (const apt of appointments) {
        // Only process pending or confirmed appointments
        if (apt.status === 'cancelled' || apt.status === 'completed') continue;
        
        // Skip if already sent auto-reminder
        if (sentAutoReminders.current.has(apt.id)) continue;
        
        // Get appointment datetime
        const aptDate = new Date(apt.date);
        const timeStr = typeof apt.time === 'string' ? apt.time : (apt.timeSlots?.[0]?.time || '00:00');
        const [hours, minutes] = timeStr.split(':').map(Number);
        aptDate.setHours(hours, minutes, 0, 0);
        
        // Check if appointment is within 15-16 minutes from now
        const timeDiff = aptDate.getTime() - now.getTime();
        if (timeDiff > 0 && timeDiff <= 16 * 60 * 1000 && timeDiff >= 14 * 60 * 1000) {
          // Mark as sent to prevent duplicates
          sentAutoReminders.current.add(apt.id);
          
          try {
            await sendReminderEmail(
              apt,
              { phone: preparer.phone, email: preparer.email, address: preparer.address },
              { name: company?.name || 'Tax Services', phone: company?.phone, email: company?.email, address: company?.address }
            );
            console.log(`✅ Auto-reminder sent to ${apt.email} for appointment at ${apt.time}`);
          } catch (err) {
            console.error(`❌ Failed to send auto-reminder to ${apt.email}:`, err);
          }
        }
      }
    };

    // Check immediately and then every minute
    checkAndSendReminders();
    const interval = setInterval(checkAndSendReminders, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [appointments, preparer, company]);

  // Push notification reminders for preparers (PWA)
  const sentPushReminders = useRef(new Set());
  useEffect(() => {
    if (!preparer?.id || !appointments.length || !notificationsEnabled) return;

    const sendPushReminders = async () => {
      const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed');
      
      if (confirmedAppointments.length === 0) return;
      
      const sent = await checkAndSendReminders(
        confirmedAppointments.map(apt => ({ ...apt, confirmed: true })),
        preparer,
        preparer.companyId
      );
      
      sent.forEach(({ appointment }) => {
        sentPushReminders.current.add(appointment.id);
      });
    };

    // Check every minute for upcoming appointments
    sendPushReminders();
    const pushInterval = setInterval(sendPushReminders, 60 * 1000);
    
    return () => clearInterval(pushInterval);
  }, [appointments, preparer, notificationsEnabled]);

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image
  const createCroppedImage = async () => {
    if (!cropImage || !croppedAreaPixels) return null;
    
    const image = new Image();
    image.src = cropImage;
    
    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 400;
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0, 0, 400, 400
        );
        const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
        resolve(croppedImage);
      };
    });
  };

  // Handle crop confirm
  const handleCropConfirm = async () => {
    const croppedImage = await createCroppedImage();
    if (croppedImage) {
      setEditForm(prev => ({ ...prev, photo: croppedImage }));
    }
    setShowCropper(false);
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size should be less than 5MB');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Consent form handlers
  const handleConsentFormChange = (e) => {
    const { name, value } = e.target;
    setConsentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSendConsent = async (e) => {
    e.preventDefault();
    if (!consentForm.taxpayerName || !consentForm.taxpayerEmail) {
      alert('Please enter taxpayer name and email');
      return;
    }

    setSendingConsent(true);
    try {
      // Generate IRS Waiver form link (direct link with company/preparer params)
      const baseUrl = window.location.origin;
      const formLink = `${baseUrl}/irs-waiver?company=${company?.id || ''}&preparer=${preparer?.id || ''}`;
      console.log('IRS Waiver form link:', formLink);

      // Send email
      console.log('Sending IRS Waiver form email...');
      await sendConsentEmail(
        consentForm.taxpayerEmail,
        consentForm.taxpayerName,
        `Action Required: Tax Documents - ${company?.name || 'Friends Tax Services LLC'}`,
        {
          preparerName: preparer.name,
          consentLink: formLink,
          taxYear: consentForm.taxYear
        },
        {
          name: company?.name || 'Friends Tax Services LLC',
          email: company?.email || '',
          phone: company?.phone || ''
        }
      );
      console.log('Email sent successfully!');

      alert('✅ IRS Waiver form link sent successfully!');
      setShowSendConsentModal(false);
      setConsentForm({
        taxpayerName: '',
        taxpayerEmail: '',
        taxpayerPhone: '',
        taxYear: new Date().getFullYear() - 1
      });
    } catch (error) {
      console.error('Error sending form link:', error);
      console.error('Error details:', error.message, error.text);
      alert(`❌ Failed to send form link: ${error.message || 'Please try again.'}`);
    } finally {
      setSendingConsent(false);
    }
  };

  const handleDeleteConsentRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this consent request?')) return;
    
    try {
      await deleteConsentRequest(requestId);
      alert('✅ Consent request deleted');
    } catch (error) {
      console.error('Error deleting consent request:', error);
      alert('❌ Failed to delete request');
    }
  };

  const copyConsentLink = (requestId) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/consent?id=${requestId}`;
    navigator.clipboard.writeText(link);
    alert('✅ Link copied to clipboard!');
  };

  const formatConsentDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ==================== Tax Returns Handlers ====================
  
  const handleTaxReturnFormChange = (e) => {
    const { name, value } = e.target;
    setTaxReturnForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitTaxReturn = async (e) => {
    e.preventDefault();
    if (!taxReturnForm.clientName || !taxReturnForm.amount) {
      alert('Please enter client name and amount');
      return;
    }

    setSavingTaxReturn(true);
    try {
      if (editingTaxReturn) {
        // Update existing
        await updateTaxReturn(editingTaxReturn.id, {
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus
        });
        alert('✅ Tax return updated successfully!');
        setEditingTaxReturn(null);
      } else {
        // Add new
        await addTaxReturn({
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus,
          preparerId: preparer.id,
          preparerName: preparer.name,
          companyId: company?.id || null
        });
        alert('✅ Tax return added successfully!');
      }
      
      // Reset form
      setTaxReturnForm({
        clientName: '',
        phone: '',
        amount: '',
        taxYear: new Date().getFullYear() - 1,
        paymentStatus: 'unpaid'
      });
    } catch (error) {
      console.error('Error saving tax return:', error);
      alert('❌ Failed to save tax return. Please try again.');
    } finally {
      setSavingTaxReturn(false);
    }
  };

  const handleEditTaxReturn = (taxReturn) => {
    setEditingTaxReturn(taxReturn);
    setTaxReturnForm({
      clientName: taxReturn.clientName,
      phone: taxReturn.phone || '',
      amount: taxReturn.amount.toString(),
      taxYear: taxReturn.taxYear,
      paymentStatus: taxReturn.paymentStatus
    });
  };

  const handleDeleteTaxReturn = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax return entry?')) return;
    try {
      await deleteTaxReturn(id);
      alert('✅ Tax return deleted');
    } catch (error) {
      console.error('Error deleting tax return:', error);
      alert('❌ Failed to delete tax return');
    }
  };

  const handleCancelEditTaxReturn = () => {
    setEditingTaxReturn(null);
    setTaxReturnForm({
      clientName: '',
      phone: '',
      amount: '',
      taxYear: new Date().getFullYear() - 1,
      paymentStatus: 'unpaid'
    });
  };

  // Calculate grand total
  const taxReturnsGrandTotal = taxReturns.reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsPaidTotal = taxReturns.filter(tr => tr.paymentStatus === 'paid').reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsUnpaidTotal = taxReturns.filter(tr => tr.paymentStatus === 'unpaid').reduce((sum, tr) => sum + (tr.amount || 0), 0);

  // ==================== PDF Document Template Handlers (kept for future use) ====================

  // Save template from builder
  // eslint-disable-next-line no-unused-vars
  const handleSaveTemplate = async (template) => {
    try {
      await saveDocumentTemplate(template, company?.id, preparer.id);
      alert('✅ Template saved successfully!');
      setShowDocumentBuilder(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('❌ Failed to save template. Please try again.');
    }
  };

  // Delete template
  // eslint-disable-next-line no-unused-vars
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteDocumentTemplate(templateId);
      alert('✅ Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('❌ Failed to delete template');
    }
  };

  // Open send document modal
  // eslint-disable-next-line no-unused-vars
  const openSendDocumentModal = (template) => {
    setSelectedTemplate(template);
    setDocumentSendForm({ clientName: '', clientEmail: '', clientPhone: '' });
    setShowSendDocumentModal(true);
  };

  // Send document to client
  const handleSendDocument = async (e) => {
    e.preventDefault();
    if (!documentSendForm.clientName || !documentSendForm.clientEmail) {
      alert('Please enter client name and email');
      return;
    }

    setSendingDocument(true);
    try {
      // Create document request
      const request = await createDocumentRequest({
        templateId: selectedTemplate.id,
        template: selectedTemplate,
        clientName: documentSendForm.clientName,
        clientEmail: documentSendForm.clientEmail,
        clientPhone: documentSendForm.clientPhone || '',
        preparerId: preparer.id,
        preparerName: preparer.name,
        companyId: company?.id || null,
        companyName: company?.name || 'Friends Tax Services LLC'
      });

      // Generate document link
      const baseUrl = window.location.origin;
      const documentLink = `${baseUrl}/document?id=${request.id}`;

      // Send email
      await sendConsentEmail(
        documentSendForm.clientEmail,
        documentSendForm.clientName,
        `Action Required: Please Sign - ${selectedTemplate.name}`,
        {
          preparerName: preparer.name,
          consentLink: documentLink,
          taxYear: new Date().getFullYear()
        },
        {
          name: company?.name || 'Friends Tax Services LLC',
          email: company?.email || '',
          phone: company?.phone || ''
        }
      );

      alert('✅ Document sent successfully!');
      setShowSendDocumentModal(false);
      setSelectedTemplate(null);
      setDocumentSendForm({ clientName: '', clientEmail: '', clientPhone: '' });
    } catch (error) {
      console.error('Error sending document:', error);
      alert(`❌ Failed to send document: ${error.message || 'Please try again.'}`);
    } finally {
      setSendingDocument(false);
    }
  };

  // Delete document request
  // eslint-disable-next-line no-unused-vars
  const handleDeleteDocumentRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this document request?')) return;
    try {
      await deleteDocumentRequest(requestId);
      alert('✅ Request deleted');
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('❌ Failed to delete request');
    }
  };

  // Copy document link
  // eslint-disable-next-line no-unused-vars
  const copyDocumentLink = (requestId) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/document?id=${requestId}`;
    navigator.clipboard.writeText(link);
    alert('✅ Link copied to clipboard!');
  };

  // Open edit profile modal
  const openEditProfile = () => {
    setEditForm({
      name: preparer.name || '',
      email: preparer.email || '',
      phone: preparer.phone || '',
      address: preparer.address || '',
      photo: preparer.photo || '',
      bio: preparer.bio || ''
    });
    setShowEditProfile(true);
  };

  // Save profile changes
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updatePreparer(preparer.id, editForm);
      
      setShowEditProfile(false);
      
      // Pass the updated preparer data to refresh UI
      if (onUpdatePreparer) {
        const updatedPreparer = { ...preparer, ...editForm };
        await onUpdatePreparer(updatedPreparer);
      }
      
      alert('✅ Profile updated successfully! Refresh the landing page to see changes.');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSavingProfile(false);
    }
  };

  // Toggle push notifications
  const handleToggleNotifications = async () => {
    if (!preparer?.id || !company?.id) {
      alert('Unable to enable notifications. Please try again.');
      return;
    }
    
    setNotificationsLoading(true);
    
    try {
      if (notificationsEnabled) {
        // Unsubscribe
        await unsubscribeFromNotifications(preparer.id, company.id);
        setNotificationsEnabled(false);
        alert('🔕 Push notifications disabled');
      } else {
        // Subscribe
        const result = await subscribeToNotifications(preparer.id, company.id);
        if (result.success) {
          setNotificationsEnabled(true);
          setNotificationPermission('granted');
          alert('🔔 Push notifications enabled! You will receive alerts when clients book appointments.');
        } else {
          if (result.error === 'Permission denied') {
            setNotificationPermission('denied');
            alert('❌ Please enable notifications in your browser settings to receive appointment alerts.');
          } else if (result.error === 'VAPID_KEY_NOT_CONFIGURED') {
            alert('⚠️ Push Notifications Setup Required\n\n' +
                  'Push notifications have not been fully configured yet.\n\n' +
                  'To enable push notifications, the platform administrator needs to:\n' +
                  '1. Go to Firebase Console > Project Settings > Cloud Messaging\n' +
                  '2. Generate a Web Push certificate (VAPID key)\n' +
                  '3. Add the VAPID key to the firebase.js file\n' +
                  '4. Deploy the Cloud Functions\n\n' +
                  'Please contact your administrator for assistance.');
          } else {
            alert(`❌ Could not enable notifications: ${result.error || result.message || 'Unknown error'}`);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const convert24to12Hour = (time24) => {
    if (!time24 || typeof time24 !== 'string') return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period} ET`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      days.push(date);
    }
    
    return days;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const hasAvailability = (date) => {
    if (!date || !preparer) return false;
    const dateStr = formatDateToString(date);
    const key = `${preparer.id}_${dateStr}`;
    const altKey = `${preparer.name}_${dateStr}`;
    const slots = availability[key] || availability[altKey];
    return slots && slots.length > 0;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const handleDateClick = (date) => {
    if (!date || isPastDate(date)) return;
    
    const dateStr = formatDateToString(date);
    const isAlreadySelected = selectedDates.some(d => formatDateToString(d) === dateStr);
    
    // Toggle date in selectedDates array for multi-date selection
    setSelectedDates(prev => {
      if (isAlreadySelected) {
        return prev.filter(d => formatDateToString(d) !== dateStr);
      } else {
        return [...prev, date].sort((a, b) => a - b);
      }
    });
    
    // Update selectedDate - clear it if we're unselecting this date
    if (isAlreadySelected) {
      // If unselecting the current selectedDate, clear it or set to another selected date
      if (selectedDate && formatDateToString(selectedDate) === dateStr) {
        const remainingDates = selectedDates.filter(d => formatDateToString(d) !== dateStr);
        setSelectedDate(remainingDates.length > 0 ? remainingDates[remainingDates.length - 1] : null);
      }
    } else {
      setSelectedDate(date);
    }
  };

  // Check if a date is in the multi-selected dates
  const isMultiSelected = (date) => {
    if (!date) return false;
    const dateStr = formatDateToString(date);
    return selectedDates.some(d => formatDateToString(d) === dateStr);
  };

  // Clear all selected dates
  const clearSelectedDates = () => {
    setSelectedDates([]);
    setSelectedDate(null);
  };

  const toggleTimeSlot = (slot) => {
    setSelectedTimeSlots(prev => {
      if (prev.includes(slot)) {
        return prev.filter(s => s !== slot);
      } else {
        return [...prev, slot].sort();
      }
    });
  };

  const selectAllSlots = () => {
    setSelectedTimeSlots([...timeSlots]);
  };

  const clearAllSlots = () => {
    setSelectedTimeSlots([]);
  };

  const selectMorningSlots = () => {
    const morning = timeSlots.filter(slot => {
      const hour = parseInt(slot.split(':')[0]);
      return hour >= 6 && hour < 12;
    });
    setSelectedTimeSlots(morning);
  };

  const selectAfternoonSlots = () => {
    const afternoon = timeSlots.filter(slot => {
      const hour = parseInt(slot.split(':')[0]);
      return hour >= 12 && hour < 17;
    });
    setSelectedTimeSlots(afternoon);
  };

  const selectEveningSlots = () => {
    const evening = timeSlots.filter(slot => {
      const hour = parseInt(slot.split(':')[0]);
      return hour >= 17 && hour <= 23;
    });
    setSelectedTimeSlots(evening);
  };

  const handleSaveAvailability = async () => {
    if (selectedDates.length === 0 || !preparer) {
      alert('Please select at least one date');
      return;
    }
    if (selectedTimeSlots.length === 0) {
      alert('Please select at least one time slot');
      return;
    }
    
    setSaving(true);
    try {
      // Save for all selected dates
      for (const date of selectedDates) {
        const dateStr = formatDateToString(date);
        await saveAvailability(preparer.name, dateStr, selectedTimeSlots, company?.id);
      }
      alert(`✅ Availability saved for ${selectedDates.length} date(s)!`);
      setSelectedDates([]);
      setSelectedDate(null);
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability. Please try again.');
    }
    setSaving(false);
  };

  // Delete availability for a specific date
  const handleDeleteAvailability = async (key) => {
    // Extract date from key format "preparerName_YYYY-MM-DD"
    const dateStr = key.includes('_') ? key.split('_').pop() : key;
    if (!window.confirm(`Delete all time slots for ${new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}?`)) {
      return;
    }
    try {
      await saveAvailability(preparer.name, dateStr, [], company?.id);
      alert('✅ Availability deleted!');
    } catch (error) {
      console.error('Error deleting availability:', error);
      alert('❌ Failed to delete availability');
    }
  };

  // Delete a single time slot from a date (updates local state, needs Save)
  const handleDeleteTimeSlot = (key, slotToRemove) => {
    // Get current slots (from edited state if exists, otherwise from availability)
    const currentSlots = editedAvailability[key] || availability[key] || [];
    const updatedSlots = currentSlots.filter(slot => slot !== slotToRemove);
    
    // Update edited availability state
    setEditedAvailability(prev => ({
      ...prev,
      [key]: updatedSlots
    }));
  };

  // Save edited availability for a specific date
  const handleSaveEditedAvailability = async (key) => {
    const dateStr = key.includes('_') ? key.split('_').pop() : key;
    const slotsToSave = editedAvailability[key];
    
    if (slotsToSave === undefined) return;
    
    try {
      await saveAvailability(preparer.name, dateStr, slotsToSave, company?.id);
      // Clear from edited state after save
      setEditedAvailability(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      alert('✅ Availability saved!');
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('❌ Failed to save availability');
    }
  };

  // Check if a date has pending edits
  const hasEditedSlots = (key) => {
    return editedAvailability[key] !== undefined;
  };

  // Get display slots (edited if exists, otherwise original)
  const getDisplaySlots = (key) => {
    return editedAvailability[key] !== undefined ? editedAvailability[key] : (availability[key] || []);
  };

  // Update appointment status handler
  const handleUpdateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await updateAppointment(appointmentId, { status: newStatus, confirmed: newStatus === 'confirmed' });
      
      // Send confirmation email when status changes to 'confirmed'
      if (newStatus === 'confirmed') {
        const apt = appointments.find(a => a.id === appointmentId);
        if (apt && (apt.email || apt.clientEmail)) {
          try {
            const emailAppointment = {
              ...apt,
              fullName: apt.fullName || apt.clientName,
              email: apt.email || apt.clientEmail,
              phone: apt.phoneNumber || apt.clientPhone,
              timeSlots: apt.timeSlots || (apt.time ? [{ date: apt.date, time: apt.time }] : [])
            };
            
            await sendConfirmationEmail(
              emailAppointment,
              { name: preparer.name, email: preparer.email, phone: preparer.phone, address: preparer.address },
              { name: company?.name, phone: company?.phone, email: company?.email, address: company?.address }
            );
            alert(`✅ Appointment ${newStatus}! Confirmation email sent.`);
            return;
          } catch (emailError) {
            console.error('Failed to send confirmation email:', emailError);
            alert(`✅ Appointment ${newStatus}! (Email notification failed - please notify client manually)`);
            return;
          }
        }
      }
      
      alert(`✅ Appointment ${newStatus}!`);
    } catch (error) {
      console.error('Error updating appointment:', error);
      if (error.code === 'unavailable' || error.message?.includes('network')) {
        alert('📶 Network error. Please check your connection and try again.');
      } else {
        alert('❌ Failed to update appointment status. Please try again.');
      }
    }
  };

  // Submit new booking handler
  // Start editing an appointment
  const handleEditAppointment = (apt) => {
    setEditingAppointment(apt);
    setOriginalEmail(apt.email || '');
    setNewBookingForm({
      clientName: apt.fullName || apt.clientName || '',
      clientEmail: apt.email || '',
      clientPhone: apt.phoneNumber || apt.clientPhone || '',
      numberOfClients: apt.numberOfClients || '1',
      fillingType: apt.fillingType || '',
      date: apt.date || '',
      time: apt.time || (apt.timeSlots && apt.timeSlots[0] && (typeof apt.timeSlots[0] === 'object' ? apt.timeSlots[0].time : apt.timeSlots[0])) || '',
      notes: apt.notes || ''
    });
    setActiveTab('new-booking');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingAppointment(null);
    setOriginalEmail('');
    setNewBookingForm({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      numberOfClients: '1',
      fillingType: '',
      date: '',
      time: '',
      notes: ''
    });
  };

  const handleSubmitNewBooking = async (e) => {
    e.preventDefault();
    
    if (!newBookingForm.clientName || !newBookingForm.date || !newBookingForm.time) {
      alert('Please fill in all required fields');
      return;
    }

    setSavingBooking(true);
    try {
      const appointmentData = {
        fullName: newBookingForm.clientName,
        email: newBookingForm.clientEmail,
        phoneNumber: newBookingForm.clientPhone,
        numberOfClients: newBookingForm.numberOfClients,
        fillingType: newBookingForm.fillingType,
        date: newBookingForm.date,
        time: newBookingForm.time,
        notes: newBookingForm.notes,
        preparer: preparer.name,
        preparerName: preparer.name
      };

      if (editingAppointment) {
        // Update existing appointment
        await updateAppointment(editingAppointment.id, appointmentData);
        
        // Only send confirmation if email was changed and new email is provided
        const emailChanged = newBookingForm.clientEmail && newBookingForm.clientEmail !== originalEmail;
        
        setBookingSuccess(true);
        setEditingAppointment(null);
        setOriginalEmail('');
        
        setTimeout(() => {
          setBookingSuccess(false);
          setActiveTab('appointments');
          if (emailChanged) {
            alert('✅ Appointment updated! Confirmation email sent to the new email address.');
          }
        }, 1500);
      } else {
        // Create new appointment
        appointmentData.preparerId = preparer.id;
        appointmentData.companyId = company?.id;
        appointmentData.status = 'confirmed';
        appointmentData.createdAt = new Date();
        appointmentData.bookedBy = 'preparer';

        const savedAppointment = await addAppointment(appointmentData);
        
        // Send confirmation email if client email is provided
        if (newBookingForm.clientEmail) {
          try {
            const emailAppointment = {
              ...savedAppointment,
              fullName: newBookingForm.clientName,
              email: newBookingForm.clientEmail,
              phone: newBookingForm.clientPhone,
              timeSlots: [newBookingForm.time]
            };
            
            await sendConfirmationEmail(
              emailAppointment,
              { name: preparer.name, email: preparer.email, phone: preparer.phone, address: preparer.address },
              { name: company?.name, phone: company?.phone, email: company?.email, address: company?.address }
            );
            console.log('✅ Confirmation email sent to:', newBookingForm.clientEmail);
          } catch (emailError) {
            console.error('Failed to send confirmation email:', emailError);
            // Don't fail the booking if email fails
          }
        }
        
        setBookingSuccess(true);
        setTimeout(() => {
          setBookingSuccess(false);
          setActiveTab('appointments');
          if (newBookingForm.clientEmail) {
            alert('✅ Appointment booked! Confirmation email sent to ' + newBookingForm.clientEmail);
          }
        }, 2000);
      }
      
      setNewBookingForm({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        numberOfClients: '1',
        fillingType: '',
        date: '',
        time: '',
        notes: ''
      });

    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('❌ Failed to save appointment');
    } finally {
      setSavingBooking(false);
    }
  };

  // Delete appointment handler
  const handleDeleteAppointment = async (appointmentId) => {
    const userConfirmed = window.confirm('Delete this appointment?');
    if (!userConfirmed) return;
    
    try {
      await deleteAppointment(appointmentId);
      alert('✅ Appointment deleted!');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('❌ Failed to delete: ' + (error.message || 'Unknown error'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#f5f5f5', color: '#666', label: 'Pending' },
      confirmed: { bg: '#e8f5e9', color: '#2e7d32', label: 'Confirmed' },
      completed: { bg: '#e3f2fd', color: '#1565c0', label: 'Completed' },
      cancelled: { bg: '#ffebee', color: '#c62828', label: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span 
        style={{ 
          background: badge.bg, 
          color: badge.color,
          padding: '4px 10px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: '500',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        {badge.label}
      </span>
    );
  };

  // Render action buttons for appointment based on status
  const renderAppointmentActions = (apt, isUpcoming = false) => {
    const actionId = apt.id;
    const buttonStyle = (bg, color, isIcon = false) => ({
      padding: isMobile ? '12px 16px' : '6px 10px',
      fontSize: isMobile ? '0.85rem' : '0.75rem',
      fontWeight: '600',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      cursor: 'pointer',
      background: bg,
      color: color,
      minHeight: isMobile ? '48px' : '36px',
      minWidth: isIcon ? '48px' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation'
    });

    // Handle missing/undefined status as 'pending'
    const status = apt.status || 'pending';
    
    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(80px, auto))', 
        gap: isMobile ? '8px' : '6px', 
        marginTop: '12px'
      }}>
        {isUpcoming && (
          <button
            onClick={() => handleEditAppointment(apt)}
            style={buttonStyle('#f5f5f5', '#1565c0')}
            title="Edit Appointment"
          >
            ✏️ Edit
          </button>
        )}
        {status === 'pending' && (
          <button
            onClick={() => handleUpdateAppointmentStatus(actionId, 'confirmed')}
            style={buttonStyle('#2e7d32', 'white')}
          >
            ✓ Confirm
          </button>
        )}
        {(status === 'pending' || status === 'confirmed') && (
          <button
            onClick={() => handleUpdateAppointmentStatus(actionId, 'completed')}
            style={buttonStyle('#1565c0', 'white')}
          >
            ✓ Complete
          </button>
        )}
        <button
          onClick={() => handleDeleteAppointment(actionId)}
          style={buttonStyle('#fff', '#c62828')}
          title="Delete Appointment"
        >
          🗑️ Delete
        </button>
      </div>
    );
  };

  // Get today's and upcoming appointments - sorted by date and time ascending (earliest first)
  // Use Eastern Time to ensure correct date comparison
  const today = toEasternInputDate(new Date());

  // No virtual expansion needed - different dates are saved as separate Firestore documents.
  // Same-date multi-client bookings remain as a single document with multiple timeSlots.
  // Apply date filter if set
  const expandedAppointments = filterDate
    ? appointments.filter(apt => apt.date === filterDate)
    : appointments;
  
  // Sort by date ascending, then by time ascending (earliest appointment first)
  const sortByDateTimeAsc = (a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    const timeA = getFirstTime(a);
    const timeB = getFirstTime(b);
    return timeA.localeCompare(timeB);
  };

  // Helper to check if appointment time has passed (for today's appointments)
  const isAppointmentTimePassed = (apt) => {
    if (apt.date !== today) return apt.date < today;
    
    const aptTime = getFirstTime(apt);
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    return aptTime < currentTime;
  };

  // Pending appointments - need confirmation from preparer
  const pendingAppointments = expandedAppointments
    .filter(apt => {
      // Only pending (or no status) appointments
      if (apt.status && apt.status !== 'pending') return false;
      // Not cancelled
      if (apt.status === 'cancelled') return false;
      // Future dates only
      if (apt.date < today) return false;
      // For today, check if time has passed
      if (apt.date === today && isAppointmentTimePassed(apt)) return false;
      return true;
    })
    .sort(sortByDateTimeAsc);

  const upcomingAppointments = expandedAppointments
    .filter(apt => {
      // Only confirmed appointments are upcoming
      if (apt.status !== 'confirmed') return false;
      // Past dates are not upcoming
      if (apt.date < today) return false;
      // For today, check if time has passed
      if (apt.date === today && isAppointmentTimePassed(apt)) return false;
      return true;
    })
    .sort(sortByDateTimeAsc);
  
  const pastAppointments = expandedAppointments
    .filter(apt => {
      // Completed appointments go to past
      if (apt.status === 'completed') return true;
      // Past dates go to past
      if (apt.date < today) return true;
      // For today, if time has passed and not cancelled, it's past
      if (apt.date === today && isAppointmentTimePassed(apt) && apt.status !== 'cancelled') return true;
      return false;
    })
    .sort(sortByDateTimeAsc); // Also ascending - oldest completed first

  const todayAppointments = expandedAppointments
    .filter(apt => apt.date === today && apt.status !== 'cancelled' && apt.status !== 'completed')
    .sort((a, b) => {
      const timeA = getFirstTime(a);
      const timeB = getFirstTime(b);
      return timeA.localeCompare(timeB);
    });

  // Pagination calculations
  const totalPendingPages = Math.ceil(pendingAppointments.length / appointmentsPerPage);
  const totalUpcomingPages = Math.ceil(upcomingAppointments.length / appointmentsPerPage);
  const totalPastPages = Math.ceil(pastAppointments.length / appointmentsPerPage);
  const totalTodayPages = Math.ceil(todayAppointments.length / appointmentsPerPage);

  const paginatedPending = pendingAppointments.slice(
    (pendingPage - 1) * appointmentsPerPage,
    pendingPage * appointmentsPerPage
  );
  const paginatedUpcoming = upcomingAppointments.slice(
    (upcomingPage - 1) * appointmentsPerPage,
    upcomingPage * appointmentsPerPage
  );
  const paginatedPast = pastAppointments.slice(
    (pastPage - 1) * appointmentsPerPage,
    pastPage * appointmentsPerPage
  );
  const paginatedToday = todayAppointments.slice(
    (todayPage - 1) * appointmentsPerPage,
    todayPage * appointmentsPerPage
  );

  // Pagination component
  const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);
    
    // Always show pagination info, but disable buttons if only 1 page
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        marginTop: '20px',
        padding: '15px',
        background: '#f8fafc',
        borderRadius: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: 'none',
            borderRadius: '8px',
            background: currentPage === 1 ? '#e2e8f0' : '#3b82f6',
            color: currentPage === 1 ? '#94a3b8' : 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '0.85em'
          }}
        >
          ⏮ First
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: 'none',
            borderRadius: '8px',
            background: currentPage === 1 ? '#e2e8f0' : '#3b82f6',
            color: currentPage === 1 ? '#94a3b8' : 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '0.85em'
          }}
        >
          ◀ Prev
        </button>
        <span style={{
          padding: '8px 16px',
          background: 'white',
          borderRadius: '8px',
          fontWeight: '600',
          color: '#334155',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          Page {currentPage} of {totalPages || 1}
        </span>
        <span style={{
          fontSize: '0.85em',
          color: '#64748b'
        }}>
          (Showing {totalItems > 0 ? startItem : 0}-{endItem} of {totalItems})
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          style={{
            padding: '8px 12px',
            border: 'none',
            borderRadius: '8px',
            background: currentPage >= totalPages ? '#e2e8f0' : '#3b82f6',
            color: currentPage >= totalPages ? '#94a3b8' : 'white',
            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '0.85em'
          }}
        >
          Next ▶
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage >= totalPages}
          style={{
            padding: '8px 12px',
            border: 'none',
            borderRadius: '8px',
            background: currentPage >= totalPages ? '#e2e8f0' : '#3b82f6',
            color: currentPage >= totalPages ? '#94a3b8' : 'white',
            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '0.85em'
          }}
        >
          Last ⏭
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f8fafc'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  // Menu counts
  const completedFormsCount = irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').length + signedConsents.length;
  const pendingFormsCount = irsWaiverSubmissions.filter(s => s.status === 'pending_verification').length;
  const menuCounts = {
    appointments: appointments.length,
    today: todayAppointments.length,
    documents: completedFormsCount + pendingFormsCount,
    taxReturns: taxReturns.length
  };

  const menuItems = getPreparerMenuItems(menuCounts);

  // Handle menu item click
  const handleMenuClick = (itemId) => {
    if (itemId === 'profile') {
      setShowEditProfile(true);
    } else {
      setActiveTab(itemId);
    }
  };

  // Open video conference in new tab
  const openVideoConference = () => {
    // Store preparer info for the video conference page
    localStorage.setItem('videoConferenceUser', JSON.stringify({
      id: preparer?.id,
      name: preparer?.name || 'Unknown Preparer',
      isPreparer: true,
      companyId: company?.id,
      companyName: company?.name
    }));
    window.open('/video-conference', '_blank');
  };

  // Footer buttons
  const footerButtons = [
    {
      label: 'Video Call',
      icon: Icons.Calendar,
      onClick: openVideoConference
    },
    // Show Admin Dashboard button if preparer is admin and callback exists
    ...(preparer?.isAdmin && onSwitchToAdmin ? [{
      label: 'Admin Panel',
      icon: Icons.Settings,
      onClick: onSwitchToAdmin,
      highlight: true
    }] : []),
    {
      label: 'Logout',
      icon: Icons.Logout,
      onClick: onLogout
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Push Notification Prompt Banner - Shows in PWA mode */}
      {showNotificationPrompt && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          style={{
            position: 'fixed',
            top: isPWAMode ? 'max(16px, env(safe-area-inset-top, 16px))' : '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderRadius: '16px',
            padding: '12px 16px',
            boxShadow: '0 10px 40px rgba(59, 130, 246, 0.4)',
            maxWidth: '92%',
            width: '380px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔔</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: '700' }}>Enable Notifications</h3>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', opacity: 0.9, lineHeight: 1.3 }}>
                Get alerts for new bookings & appointment reminders.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    setNotificationsLoading(true);
                    try {
                      const result = await subscribeToNotifications(preparer.id, company.id);
                      setNotificationsLoading(false);
                      if (result.success) {
                        setNotificationsEnabled(true);
                        setNotificationPermission('granted');
                        setShowNotificationPrompt(false);
                        alert('✅ Notifications enabled! You will receive alerts for new bookings.');
                      } else {
                        // Show detailed error
                        const errorMsg = result.error === 'Permission denied' 
                          ? '❌ Please allow notifications when prompted by your browser.'
                          : result.error === 'VAPID_KEY_NOT_CONFIGURED'
                          ? '❌ Push notifications not configured. Contact administrator.'
                          : `❌ Could not enable notifications: ${result.error || 'Unknown error'}`;
                        alert(errorMsg);
                      }
                    } catch (err) {
                      setNotificationsLoading(false);
                      alert('❌ Error enabling notifications: ' + err.message);
                    }
                  }}
                  disabled={notificationsLoading}
                  style={{
                    padding: '8px 16px',
                    background: 'white',
                    color: '#1d4ed8',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    cursor: notificationsLoading ? 'wait' : 'pointer',
                    opacity: notificationsLoading ? 0.7 : 1
                  }}
                >
                  {notificationsLoading ? '...' : '✓ Enable'}
                </button>
                <button
                  onClick={() => {
                    setShowNotificationPrompt(false);
                    localStorage.setItem('notificationPromptDismissed', Date.now().toString());
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '500',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  Later
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setShowNotificationPrompt(false);
                localStorage.setItem('notificationPromptDismissed', Date.now().toString());
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '1.1rem',
                cursor: 'pointer',
                padding: '0',
                opacity: 0.7
              }}
            >×</button>
          </div>
        </motion.div>
      )}

      {/* Modern Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        menuItems={menuItems}
        activeItem={activeTab}
        onItemClick={handleMenuClick}
        footerButtons={footerButtons}
        userInfo={{
          name: preparer?.name || 'Preparer',
          role: preparer?.isAdmin ? 'Admin' : 'Tax Preparer',
          photo: preparer?.photo,
          initials: preparer?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'P'
        }}
        headerInfo={{
          logo: company?.logo ? (
            <img src={company.logo} alt={company.name} style={{ height: '32px', width: 'auto' }} />
          ) : (
            <span style={{ fontSize: '20px' }}>🏢</span>
          ),
          title: company?.name || 'Friends Tax Services LLC',
          subtitle: 'Preparer Dashboard'
        }}
      />

      {/* Main Content */}
      <MainContent
        isSidebarCollapsed={isSidebarCollapsed}
        onMenuClick={() => setIsSidebarOpen(true)}
        headerTitle="Preparer Dashboard"
        headerSubtitle={`Welcome back, ${preparer?.name || 'Preparer'}`}
        userInfo={{
          name: preparer?.name || 'Preparer',
          role: preparer?.isAdmin ? 'Admin' : 'Tax Preparer',
          photo: preparer?.photo
        }}
        companyInfo={{
          name: company?.name || 'Friends Tax Services LLC',
          logo: company?.logo
        }}
        headerActions={
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => {
                const bookingUrl = `${window.location.origin}/${company?.slug || 'book'}`;
                navigator.clipboard.writeText(bookingUrl);
                alert('✅ Booking link copied!\n\n' + bookingUrl + '\n\nShare this link with clients to book appointments.');
              }}
              style={{ 
                fontSize: '0.875rem', 
                padding: '10px 18px', 
                borderRadius: '8px', 
                background: '#10b981', 
                color: 'white', 
                border: 'none', 
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}
            >
              <span>🔗</span>
              <span>Copy Booking Link</span>
            </button>
            <button 
              onClick={openVideoConference}
              style={{ 
                fontSize: '0.875rem', 
                padding: '10px 18px', 
                borderRadius: '8px', 
                background: '#8b5cf6', 
                color: 'white', 
                border: 'none', 
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
              }}
            >
              <span>📹</span>
              <span>Video Call</span>
            </button>
            <button 
              onClick={onLogout}
              style={{ 
                fontSize: '0.875rem', 
                padding: '10px 18px', 
                borderRadius: '8px', 
                background: '#f1f5f9', 
                color: '#64748b', 
                border: '1px solid #e2e8f0', 
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>Logout</span>
            </button>
          </div>
        }
      >
        {/* Stats Grid - Real Data */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', 
          gap: '10px', 
          marginBottom: '16px' 
        }}>
          <StatCard
            title="Pending"
            value={pendingAppointments.length}
            icon={Icons.Clock}
            color="orange"
            onClick={() => setActiveTab('appointments')}
          />
          <StatCard
            title="Confirmed"
            value={upcomingAppointments.length}
            icon={Icons.Calendar}
            color="green"
            onClick={() => setActiveTab('appointments')}
          />
          <StatCard
            title="Today"
            value={todayAppointments.length}
            icon={Icons.Calendar}
            color="blue"
            onClick={() => setActiveTab('today')}
          />
          <StatCard
            title="Consents"
            value={completedFormsCount}
            icon={Icons.Document}
            color="purple"
            onClick={() => setActiveTab('documents')}
          />
          <StatCard
            title="Returns"
            value={taxReturns.length}
            icon={Icons.DollarSign}
            color="gray"
            onClick={() => setActiveTab('taxreturns')}
          />
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <ContentCard>
              {/* Appointments Tab */}
              {activeTab === 'appointments' && (
                <div style={{ padding: '0' }}>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: isSmallMobile ? 'column' : 'row',
                    alignItems: isSmallMobile ? 'flex-start' : 'center', 
                    justifyContent: 'space-between', 
                    marginBottom: '20px',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <h2 style={{ 
                      fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', 
                      fontWeight: '700', 
                      color: '#1e293b', 
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      📋 All Appointments
                    </h2>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Date Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="date"
                          value={filterDate}
                          onChange={(e) => {
                            setFilterDate(e.target.value);
                            setPendingPage(1);
                            setUpcomingPage(1);
                            setPastPage(1);
                          }}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.85rem',
                            color: '#334155',
                            background: '#f8fafc',
                            cursor: 'pointer',
                            minHeight: '36px'
                          }}
                        />
                        {filterDate && (
                          <button
                            onClick={() => {
                              setFilterDate('');
                              setPendingPage(1);
                              setUpcomingPage(1);
                              setPastPage(1);
                            }}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              background: '#fee2e2',
                              color: '#dc2626',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              minHeight: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>
                      <span style={{ 
                        padding: '6px 12px', 
                        borderRadius: '20px', 
                        fontSize: '0.8rem', 
                        fontWeight: '600',
                        background: '#fef3c7', 
                        color: '#92400e' 
                      }}>
                        {pendingAppointments.length} Pending
                      </span>
                      <span style={{ 
                        padding: '6px 12px', 
                        borderRadius: '20px', 
                        fontSize: '0.8rem', 
                        fontWeight: '600',
                        background: '#dcfce7', 
                        color: '#166534' 
                      }}>
                        {upcomingAppointments.length} Confirmed
                      </span>
                      <span style={{ 
                        padding: '6px 12px', 
                        borderRadius: '20px', 
                        fontSize: '0.8rem', 
                        fontWeight: '600',
                        background: '#f1f5f9', 
                        color: '#475569' 
                      }}>
                        {pastAppointments.length} Past
                      </span>
                    </div>
                  </div>
            
            {filterDate && (
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <span style={{ color: '#1e40af', fontSize: '0.9rem', fontWeight: '600' }}>
                  📅 Showing appointments for {new Date(filterDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <span style={{ color: '#3b82f6', fontSize: '0.85rem' }}>
                  {expandedAppointments.length} appointment(s) found
                </span>
              </div>
            )}

            {(filterDate ? expandedAppointments.length === 0 : appointments.length === 0) ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px',
                border: '2px dashed #e2e8f0'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>{filterDate ? '🔍' : '📭'}</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: '0 0 8px' }}>
                  {filterDate ? 'No Appointments on This Date' : 'No Appointments Yet'}
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: '300px', margin: '0 auto' }}>
                  {filterDate 
                    ? `No appointments found for ${new Date(filterDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`
                    : 'Your appointments will appear here when clients book with you.'}
                </p>
                {filterDate && (
                  <button
                    onClick={() => setFilterDate('')}
                    style={{
                      marginTop: '16px',
                      padding: '8px 20px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Show All Appointments
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Pending Appointments - Need Confirmation */}
                {pendingAppointments.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    {/* Section Header */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem'
                        }}>⏳</div>
                        <div>
                          <h3 style={{ 
                            margin: 0, 
                            color: '#333', 
                            fontSize: '1.1rem', 
                            fontWeight: '600'
                          }}>Pending Confirmation</h3>
                          <p style={{ 
                            margin: '2px 0 0 0', 
                            color: '#f59e0b', 
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}>⚠️ {pendingAppointments.length} booking(s) need your confirmation</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            const allPendingIds = pendingAppointments.map(a => a.id);
                            const allSelected = allPendingIds.every(id => selectedAppointments.includes(id));
                            if (allSelected) {
                              setSelectedAppointments(prev => prev.filter(id => !allPendingIds.includes(id)));
                            } else {
                              setSelectedAppointments(prev => [...new Set([...prev, ...allPendingIds])]);
                            }
                          }}
                          style={{
                            background: pendingAppointments.every(a => selectedAppointments.includes(a.id)) ? '#1565c0' : '#f5f5f5',
                            color: pendingAppointments.every(a => selectedAppointments.includes(a.id)) ? 'white' : '#333',
                            border: '1px solid #e0e0e0',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            minWidth: '110px',
                            minHeight: '44px',
                            touchAction: 'manipulation'
                          }}
                        >
                          {pendingAppointments.every(a => selectedAppointments.includes(a.id)) ? '✓ All Selected' : 'Select All'}
                        </button>
                      </div>
                    </div>

                    {/* Pending Appointments List */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isExtraSmallMobile ? '1fr' : (isTablet ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'repeat(auto-fill, minmax(320px, 1fr))'),
                      gap: '16px'
                    }}>
                      {paginatedPending.map(apt => (
                        <div 
                          key={apt.id}
                          style={{
                            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                            borderRadius: '16px',
                            padding: '20px',
                            border: '2px solid #f59e0b',
                            position: 'relative',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {/* Split appointment badge */}
                          {apt.groupId && (
                            <div style={{
                              background: '#f59e0b',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              padding: '2px 10px',
                              borderRadius: '8px',
                              display: 'inline-block',
                              marginBottom: '8px'
                            }}>
                              Part of {apt.totalGroupClients}-client group booking
                            </div>
                          )}
                          {/* Selection Checkbox */}
                          <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                            <input 
                              type="checkbox"
                              checked={selectedAppointments.includes(apt.id)}
                              onChange={(e) => {
                                const aptId = apt.id;
                                if (e.target.checked) {
                                  setSelectedAppointments(prev => [...prev, aptId]);
                                } else {
                                  setSelectedAppointments(prev => prev.filter(id => id !== aptId));
                                }
                              }}
                              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            />
                          </div>

                          {/* Client Info */}
                          <div style={{ marginBottom: '16px', paddingRight: '30px' }}>
                            <h4 style={{ 
                              margin: '0 0 4px 0', 
                              fontSize: '1.1rem', 
                              fontWeight: '600', 
                              color: '#1e293b' 
                            }}>
                              {apt.fullName || apt.clientName || 'Client'}
                            </h4>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                              📅 {formatDate(apt.date)} at {formatTimeSlots(apt)}
                            </p>
                            <p style={{ margin: '4px 0 0 0', color: '#1e40af', fontSize: '0.85rem', fontWeight: '600' }}>
                              👥 {apt.numberOfClients || '1'} Client(s)
                            </p>
                            {apt.phone && (
                              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                                📱 {apt.phone}
                              </p>
                            )}
                            {apt.email && (
                              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                                ✉️ {apt.email}
                              </p>
                            )}
                          </div>

                          {/* Service Type */}
                          {apt.serviceType && (
                            <div style={{ 
                              background: 'white', 
                              padding: '8px 12px', 
                              borderRadius: '8px', 
                              marginBottom: '16px',
                              fontSize: '0.85rem',
                              color: '#475569'
                            }}>
                              🏷️ {apt.serviceType}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleUpdateAppointmentStatus(apt.id, 'confirmed')}
                              style={{
                                flex: 1,
                                padding: '12px 16px',
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: '48px',
                                touchAction: 'manipulation',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                              }}
                            >
                              ✓ Confirm
                            </button>
                            <button
                              onClick={() => handleDeleteAppointment(apt.id)}
                              style={{
                                padding: '12px 16px',
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: '48px',
                                touchAction: 'manipulation'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination for Pending */}
                    {totalPendingPages > 1 && (
                      <PaginationControls 
                        currentPage={pendingPage}
                        totalPages={totalPendingPages}
                        onPageChange={setPendingPage}
                        totalItems={pendingAppointments.length}
                        itemsPerPage={appointmentsPerPage}
                      />
                    )}
                  </div>
                )}

                {/* Upcoming 7 Days Overview */}
                {(() => {
                  const now = new Date();
                  const weekDays = [];
                  for (let i = 0; i < 7; i++) {
                    const d = new Date(now);
                    d.setDate(now.getDate() + i);
                    const dateStr = toEasternInputDate(d);
                    const dayAppts = expandedAppointments.filter(apt => apt.date === dateStr && apt.status !== 'cancelled');
                    const clientCount = dayAppts.reduce((sum, apt) => sum + (parseInt(apt.numberOfClients) || 1), 0);
                    weekDays.push({
                      dayName: i === 0 ? 'TODAY' : d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short' }).toUpperCase(),
                      date: d,
                      dateStr,
                      count: dayAppts.length,
                      clientCount,
                      isToday: i === 0
                    });
                  }
                  const startLabel = weekDays[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const endLabel = weekDays[6].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return (
                    <div style={{ marginBottom: '24px' }}>
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '1.2rem' }}>📅</span>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>Upcoming 7 Days</h3>
                        <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>{startLabel} - {endLabel}</span>
                      </div>
                      {/* Day Cards - horizontally scrollable */}
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        paddingBottom: '4px',
                        margin: '0 -4px',
                        padding: '0 4px 4px 4px'
                      }}>
                        {weekDays.map((day, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              if (filterDate === day.dateStr) {
                                setFilterDate('');
                              } else {
                                setFilterDate(day.dateStr);
                              }
                              setPendingPage(1);
                              setUpcomingPage(1);
                              setPastPage(1);
                            }}
                            style={{
                              flex: '0 0 auto',
                              width: isExtraSmallMobile ? '52px' : '58px',
                              padding: isExtraSmallMobile ? '8px 4px' : '10px 6px',
                              borderRadius: '12px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              background: day.isToday
                                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                                : filterDate === day.dateStr
                                ? '#e0e7ff'
                                : '#f8fafc',
                              border: filterDate === day.dateStr ? '2px solid #3b82f6' : '1.5px solid #e2e8f0',
                              color: day.isToday ? 'white' : '#334155',
                              transition: 'all 0.2s ease',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            <div style={{ fontSize: '0.6rem', fontWeight: '700', letterSpacing: '0.5px', marginBottom: '2px', opacity: day.isToday ? 1 : 0.7 }}>
                              {day.dayName}
                            </div>
                            <div style={{ fontSize: '1.15rem', fontWeight: '800', lineHeight: 1.2 }}>
                              {day.date.getDate()}
                            </div>
                            <div style={{ fontSize: '0.6rem', fontWeight: '600', opacity: 0.8, marginBottom: '4px' }}>
                              {day.date.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                            <div style={{
                              fontSize: '0.6rem',
                              fontWeight: '700',
                              padding: '2px 0',
                              borderRadius: '6px',
                              background: day.isToday ? 'rgba(255,255,255,0.25)' : (day.count > 0 ? '#dbeafe' : '#f1f5f9'),
                              color: day.isToday ? 'white' : (day.count > 0 ? '#1d4ed8' : '#94a3b8')
                            }}>
                              {day.count > 0 ? `${day.count} appt${day.count !== 1 ? 's' : ''}` : '0 appts'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Confirmed Appointments */}
                {upcomingAppointments.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    {/* Section Header */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                      }}>
                        <div>
                          <h3 style={{ 
                            margin: 0, 
                            color: '#333', 
                            fontSize: '1.1rem', 
                            fontWeight: '600'
                          }}>Confirmed Appointments</h3>
                          <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '0.8rem' }}>Sorted by earliest first</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            const allUpcomingIds = upcomingAppointments.map(a => a.id);
                            const allSelected = allUpcomingIds.every(id => selectedAppointments.includes(id));
                            if (allSelected) {
                              setSelectedAppointments(prev => prev.filter(id => !allUpcomingIds.includes(id)));
                            } else {
                              setSelectedAppointments(prev => [...new Set([...prev, ...allUpcomingIds])]);
                            }
                          }}
                          style={{
                            background: upcomingAppointments.every(a => selectedAppointments.includes(a.id)) ? '#1565c0' : '#f5f5f5',
                            color: upcomingAppointments.every(a => selectedAppointments.includes(a.id)) ? 'white' : '#333',
                            border: '1px solid #e0e0e0',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            minWidth: '110px',
                            textAlign: 'center'
                          }}
                        >
                          {upcomingAppointments.every(a => selectedAppointments.includes(a.id)) ? '☑ Deselect All' : '☐ Select All'}
                        </button>
                        <button
                          onClick={async () => {
                            const selectedUpcoming = upcomingAppointments.filter(a => selectedAppointments.includes(a.id));
                            if (selectedUpcoming.length === 0) return;
                            if (!window.confirm(`Send reminder to ${selectedUpcoming.length} selected appointment(s)?`)) return;
                            let sent = 0, failed = 0;
                            for (const apt of selectedUpcoming) {
                              try {
                                await sendReminderEmail(
                                  apt,
                                  { phone: preparer.phone, email: preparer.email, address: preparer.address },
                                  { name: company?.name || 'Tax Services', phone: company?.phone, email: company?.email, address: company?.address }
                                );
                                sent++;
                              } catch (err) {
                                failed++;
                              }
                            }
                            alert(`✅ Sent ${sent} reminder${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`);
                            setSelectedAppointments(prev => prev.filter(id => !selectedUpcoming.map(a => a.id).includes(id)));
                          }}
                          disabled={selectedAppointments.filter(id => upcomingAppointments.some(a => a.id === id)).length === 0}
                          style={{
                            background: selectedAppointments.filter(id => upcomingAppointments.some(a => a.id === id)).length > 0 ? '#1565c0' : '#e0e0e0',
                            color: selectedAppointments.filter(id => upcomingAppointments.some(a => a.id === id)).length > 0 ? 'white' : '#999',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: selectedAppointments.filter(id => upcomingAppointments.some(a => a.id === id)).length > 0 ? 'pointer' : 'not-allowed',
                            minWidth: '130px',
                            textAlign: 'center'
                          }}
                        >
                          ✉️ Send Selected ({selectedAppointments.filter(id => upcomingAppointments.some(a => a.id === id)).length})
                        </button>
                        <div style={{ 
                          background: '#f5f5f5', 
                          color: '#333', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          border: '1px solid #e0e0e0'
                        }}>
                          {upcomingAppointments.length} Scheduled
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {paginatedUpcoming.map((apt, index) => {
                        const prevApt = index > 0 ? paginatedUpcoming[index - 1] : null;
                        const showDateHeader = !prevApt || prevApt.date !== apt.date;
                        const dateObj = new Date(apt.date + 'T00:00:00');
                        const isDateToday = apt.date === today;
                        const tomorrowDate = (() => { const t = new Date(); t.setDate(t.getDate() + 1); return toEasternInputDate(t); })();
                        const isDateTomorrow = apt.date === tomorrowDate;
                        const dayLabel = isDateToday ? 'Today' : isDateTomorrow ? 'Tomorrow' : dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                        const aptsOnDate = upcomingAppointments.filter(a => a.date === apt.date).length;
                        return (
                        <React.Fragment key={apt.id}>
                        {showDateHeader && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 16px',
                            marginTop: index > 0 ? '8px' : '0',
                            background: isDateToday ? 'linear-gradient(135deg, #dbeafe, #eff6ff)' : '#f8fafc',
                            borderRadius: '10px',
                            border: isDateToday ? '1px solid #93c5fd' : '1px solid #e2e8f0'
                          }}>
                            <div style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '10px',
                              background: isDateToday ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <span style={{ fontSize: '0.6rem', fontWeight: '700', color: isDateToday ? '#bfdbfe' : '#94a3b8', lineHeight: 1, textTransform: 'uppercase' }}>
                                {dateObj.toLocaleDateString('en-US', { month: 'short' })}
                              </span>
                              <span style={{ fontSize: '1rem', fontWeight: '800', color: isDateToday ? 'white' : '#334155', lineHeight: 1.1 }}>
                                {dateObj.getDate()}
                              </span>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '700', fontSize: '0.9rem', color: isDateToday ? '#1e40af' : '#1e293b' }}>
                                {dayLabel}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: isDateToday ? '#3b82f6' : '#64748b' }}>
                                {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}
                              </div>
                            </div>
                            <div style={{ background: isDateToday ? '#3b82f6' : '#e2e8f0', color: isDateToday ? 'white' : '#64748b', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', flexShrink: 0 }}>
                              {aptsOnDate}
                            </div>
                          </div>
                        )}
                        <div style={{ 
                          position: 'relative',
                          background: selectedAppointments.includes(apt.id) ? '#e3f2fd' : 'white',
                          borderRadius: '8px',
                          border: selectedAppointments.includes(apt.id) ? '2px solid #1565c0' : '1px solid #e0e0e0',
                          overflow: 'hidden'
                        }}>                          {/* Split appointment badge */}
                          {apt.groupId && (
                            <div style={{
                              background: '#3b82f6',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              padding: '2px 10px',
                              textAlign: 'center'
                            }}>
                              Part of {apt.totalGroupClients}-client group booking
                            </div>
                          )}
                          {/* Simple Card Header */}
                          <div style={{
                            background: '#f5f5f5',
                            padding: isMobile ? '10px 12px' : '12px 16px',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            <div style={{ 
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', minWidth: 0, flex: 1 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedAppointments.includes(apt.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const aptId = apt.id;
                                    setSelectedAppointments(prev => 
                                      prev.includes(aptId) 
                                        ? prev.filter(id => id !== aptId)
                                        : [...prev, aptId]
                                    );
                                  }}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <div style={{
                                  width: isMobile ? '28px' : '32px',
                                  height: isMobile ? '28px' : '32px',
                                  background: '#e0e0e0',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#666',
                                  fontWeight: '600',
                                  fontSize: isMobile ? '0.75rem' : '0.85rem',
                                  flexShrink: 0
                                }}>
                                  #{(upcomingPage - 1) * appointmentsPerPage + index + 1}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ color: '#333', fontSize: isMobile ? '0.8rem' : '0.9rem', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {new Date(apt.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </div>
                                  <div style={{ color: '#666', fontSize: isMobile ? '0.7rem' : '0.8rem', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {formatTimeSlots(apt)}
                                  </div>
                                </div>
                              </div>
                              <div style={{ flexShrink: 0 }}>{getStatusBadge(apt.status)}</div>
                            </div>
                          </div>
                          
                          {/* Card Body */}
                          <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#e0e0e0',
                                color: '#666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '600',
                                fontSize: '1rem',
                                flexShrink: 0
                              }}>
                                {apt.fullName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apt.fullName}</h4>
                                {apt.fillingType && <span style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px', display: 'block' }}>{apt.fillingType}</span>}
                              </div>
                            </div>
                            
                            {/* Contact Info */}
                            <div style={{ 
                              fontSize: '0.85rem',
                              color: '#666',
                              marginBottom: '12px'
                            }}>
                              <div style={{ marginBottom: '4px' }}>
                                <a href={`mailto:${apt.email}`} style={{ color: '#666', textDecoration: 'none' }}>
                                  {apt.email}
                                </a>
                              </div>
                              <div>
                                <a href={`tel:${apt.phoneNumber}`} style={{ color: '#666', textDecoration: 'none' }}>
                                  {apt.phoneNumber}
                                </a>
                              </div>
                            </div>
                            
                            <div style={{ 
                              fontSize: '0.8rem', 
                              color: '#1565c0',
                              fontWeight: '600',
                              marginBottom: '12px'
                            }}>
                              👥 {apt.numberOfClients || '1'} Client(s)
                            </div>
                            {/* Action Buttons */}
                            {renderAppointmentActions(apt, true)}
                          </div>
                        </div>
                        </React.Fragment>
                        );
                      })}
                    </div>
                    <PaginationControls
                      currentPage={upcomingPage}
                      totalPages={totalUpcomingPages}
                      onPageChange={setUpcomingPage}
                      totalItems={upcomingAppointments.length}
                      itemsPerPage={appointmentsPerPage}
                    />
                  </div>
                )}

                {/* Past Appointments */}
                {pastAppointments.length > 0 && (
                  <div style={{ marginTop: '32px' }}>
                    {/* Collapsible Section Header */}
                    <div 
                      onClick={() => setShowPastAppointments(!showPastAppointments)}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: showPastAppointments ? '20px' : '0',
                        padding: '12px 16px',
                        background: '#f5f5f5',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                      }}>
                        <div>
                          <h3 style={{ 
                            margin: 0, 
                            color: '#333', 
                            fontSize: '1rem', 
                            fontWeight: '500'
                          }}>Past Appointments</h3>
                          <p style={{ 
                            margin: '2px 0 0 0', 
                            color: '#666', 
                            fontSize: '0.8rem' 
                          }}>Click to {showPastAppointments ? 'hide' : 'show'}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                          background: '#e0e0e0', 
                          color: '#333', 
                          padding: '4px 10px', 
                          borderRadius: '4px', 
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {pastAppointments.length}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: '#666',
                          transform: showPastAppointments ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}>
                          ▼
                        </div>
                      </div>
                    </div>
                    
                    {showPastAppointments && (<>
                    {/* Selection Controls */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          const allPastIds = pastAppointments.map(a => a.id);
                          const allSelected = allPastIds.every(id => selectedAppointments.includes(id));
                          if (allSelected) {
                            setSelectedAppointments(prev => prev.filter(id => !allPastIds.includes(id)));
                          } else {
                            setSelectedAppointments(prev => [...new Set([...prev, ...allPastIds])]);
                          }
                        }}
                        style={{
                          background: pastAppointments.every(a => selectedAppointments.includes(a.id)) ? '#1565c0' : '#f5f5f5',
                          color: pastAppointments.every(a => selectedAppointments.includes(a.id)) ? 'white' : '#333',
                          border: '1px solid #e0e0e0',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          minWidth: '110px',
                          textAlign: 'center'
                        }}
                      >
                        {pastAppointments.every(a => selectedAppointments.includes(a.id)) ? '☑ Deselect All' : '☐ Select All'}
                      </button>
                      <button
                        onClick={async () => {
                          const selectedPast = pastAppointments.filter(a => selectedAppointments.includes(a.id));
                          if (selectedPast.length === 0) return;
                          if (!window.confirm(`Send Tax Season 2026 booking invitation to ${selectedPast.length} selected past client(s)?`)) return;
                          
                          // Build booking link
                          const bookingLink = company?.slug 
                            ? `${window.location.origin}/company/${company.slug}` 
                            : window.location.origin;
                          
                          let sent = 0, failed = 0;
                          for (const apt of selectedPast) {
                            try {
                              await sendTaxSeasonEmail(
                                apt,
                                { name: preparer.name, phone: preparer.phone, email: preparer.email },
                                { name: company?.name || 'Tax Services', phone: company?.phone, email: company?.email, address: company?.address },
                                bookingLink
                              );
                              sent++;
                            } catch (err) {
                              failed++;
                            }
                          }
                          alert(`✅ Sent ${sent} invitation${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`);
                          setSelectedAppointments(prev => prev.filter(id => !selectedPast.map(a => a.id).includes(id)));
                        }}
                        disabled={selectedAppointments.filter(id => pastAppointments.some(a => a.id === id)).length === 0}
                        style={{
                          background: selectedAppointments.filter(id => pastAppointments.some(a => a.id === id)).length > 0 ? '#2e7d32' : '#e0e0e0',
                          color: selectedAppointments.filter(id => pastAppointments.some(a => a.id === id)).length > 0 ? 'white' : '#999',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          cursor: selectedAppointments.filter(id => pastAppointments.some(a => a.id === id)).length > 0 ? 'pointer' : 'not-allowed',
                          minWidth: '130px',
                          textAlign: 'center'
                        }}
                      >
                        📅 Invite to Book ({selectedAppointments.filter(id => pastAppointments.some(a => a.id === id)).length})
                      </button>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                      gap: '16px' 
                    }}>
                      {paginatedPast.map((apt, index) => (
                        <div key={apt.id} style={{ 
                          position: 'relative',
                          background: selectedAppointments.includes(apt.id) ? '#e3f2fd' : 'white',
                          borderRadius: '4px',
                          border: selectedAppointments.includes(apt.id) ? '2px solid #1565c0' : '1px solid #e0e0e0',
                          overflow: 'hidden'
                        }}>
                          {/* Split appointment badge */}
                          {apt.groupId && (
                            <div style={{
                              background: '#64748b',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              padding: '2px 10px',
                              textAlign: 'center'
                            }}>
                              Part of {apt.totalGroupClients}-client group booking
                            </div>
                          )}
                          {/* Simple header */}
                          <div style={{
                            background: '#f5f5f5',
                            padding: '10px 14px',
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '10px', minWidth: 0, flex: 1 }}>
                              <input
                                type="checkbox"
                                checked={selectedAppointments.includes(apt.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const aptId = apt.id;
                                  setSelectedAppointments(prev => 
                                    prev.includes(aptId) 
                                      ? prev.filter(id => id !== aptId)
                                      : [...prev, aptId]
                                  );
                                }}
                                style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                              />
                              <div style={{
                                width: '24px',
                                height: '24px',
                                background: '#e0e0e0',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#666',
                                fontWeight: '500',
                                fontSize: '0.75rem',
                                flexShrink: 0
                              }}>
                                {(pastPage - 1) * appointmentsPerPage + index + 1}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#333', fontSize: isMobile ? '0.8rem' : '0.85rem', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {new Date(apt.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                </div>
                                <div style={{ color: '#666', fontSize: isMobile ? '0.7rem' : '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {formatTimeSlots(apt)}
                                </div>
                              </div>
                            </div>
                            <div style={{ flexShrink: 0 }}>{getStatusBadge(apt.status)}</div>
                          </div>
                          
                          {/* Card body */}
                          <div style={{ padding: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: '#e0e0e0',
                                color: '#666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '500',
                                fontSize: '0.9rem',
                                flexShrink: 0
                              }}>
                                {apt.fullName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '500', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apt.fullName}</h4>
                                {apt.fillingType && <span style={{ fontSize: '0.75rem', color: '#666' }}>{apt.fillingType}</span>}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '10px' }}>
                              <div style={{ marginBottom: '2px' }}>{apt.email}</div>
                              <div>{apt.phoneNumber}</div>
                            </div>
                            <div style={{ 
                              fontSize: '0.8rem', 
                              color: '#64748b',
                              fontWeight: '600',
                              marginBottom: '10px'
                            }}>
                              👥 {apt.numberOfClients || '1'} Client(s)
                            </div>
                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <a href={`tel:${apt.phoneNumber}`} style={{ 
                                padding: isMobile ? '12px 16px' : '8px 12px', 
                                fontSize: isMobile ? '0.85rem' : '0.8rem', 
                                fontWeight: '600', 
                                border: '1px solid #e0e0e0', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                background: '#f0f9ff', 
                                color: '#0369a1', 
                                textDecoration: 'none',
                                minHeight: isMobile ? '44px' : '36px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                WebkitTapHighlightColor: 'transparent'
                              }}>📞 Call</a>
                              <a href={`mailto:${apt.email}`} style={{ 
                                padding: isMobile ? '12px 16px' : '8px 12px', 
                                fontSize: isMobile ? '0.85rem' : '0.8rem', 
                                fontWeight: '600', 
                                border: '1px solid #e0e0e0', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                background: '#f0fdf4', 
                                color: '#15803d', 
                                textDecoration: 'none',
                                minHeight: isMobile ? '44px' : '36px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                WebkitTapHighlightColor: 'transparent'
                              }}>✉️ Email</a>
                              <button
                                onClick={async () => {
                                  const today = new Date();
                                  const defaultDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
                                  const newDate = prompt('Enter new date (MM/DD/YYYY) to reschedule:', defaultDate);
                                  if (newDate && newDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                    // Convert MM/DD/YYYY to YYYY-MM-DD for database
                                    const [month, day, year] = newDate.split('/');
                                    const dbDate = `${year}-${month}-${day}`;
                                    try {
                                      await updateAppointment(apt.id, { 
                                        date: dbDate, 
                                        status: 'scheduled' 
                                      });
                                      alert('✅ Appointment rescheduled to ' + newDate + '. It will appear in Upcoming Appointments.');
                                    } catch (error) {
                                      alert('Failed to reschedule appointment');
                                    }
                                  } else if (newDate) {
                                    alert('Please use format MM/DD/YYYY (e.g., 02/06/2026)');
                                  }
                                }}
                                style={{ 
                                  padding: isMobile ? '12px 16px' : '8px 12px', 
                                  fontSize: isMobile ? '0.85rem' : '0.8rem', 
                                  fontWeight: '600', 
                                  border: '1px solid #e0e0e0', 
                                  borderRadius: '8px', 
                                  cursor: 'pointer', 
                                  background: '#fff', 
                                  color: '#1565c0',
                                  minHeight: isMobile ? '44px' : '36px',
                                  WebkitTapHighlightColor: 'transparent',
                                  touchAction: 'manipulation'
                                }}
                              >Reschedule</button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Delete this appointment?')) {
                                    try {
                                      await deleteAppointment(apt.id);
                                      alert('✅ Appointment deleted');
                                    } catch (error) {
                                      alert('Failed to delete appointment');
                                    }
                                  }
                                }}
                                style={{ 
                                  padding: isMobile ? '12px 16px' : '8px 12px', 
                                  fontSize: isMobile ? '0.85rem' : '0.8rem', 
                                  fontWeight: '600', 
                                  border: '1px solid #fecaca', 
                                  borderRadius: '8px', 
                                  cursor: 'pointer', 
                                  background: '#fef2f2', 
                                  color: '#dc2626',
                                  minHeight: isMobile ? '44px' : '36px',
                                  WebkitTapHighlightColor: 'transparent',
                                  touchAction: 'manipulation'
                                }}
                              >🗑️ Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <PaginationControls
                      currentPage={pastPage}
                      totalPages={totalPastPages}
                      onPageChange={setPastPage}
                      totalItems={pastAppointments.length}
                      itemsPerPage={appointmentsPerPage}
                    />
                    </>)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Today Tab */}
        {activeTab === 'today' && (
          <div style={{ padding: '0' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', 
              fontWeight: '700', 
              color: '#1e293b', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              📅 Today's Schedule - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>

            {/* Today's Quick Stats */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                flex: '1',
                minWidth: '120px',
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '14px',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  color: 'white'
                }}>
                  📋
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1e40af' }}>{todayAppointments.length}</div>
                  <div style={{ fontSize: '0.75rem', color: '#1d4ed8', fontWeight: '600' }}>Appointments</div>
                </div>
              </div>
              <div style={{
                flex: '1',
                minWidth: '120px',
                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                border: '1px solid #c084fc',
                borderRadius: '14px',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('documents')}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  color: 'white'
                }}>
                  ✅
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#7c3aed' }}>{completedFormsCount}</div>
                  <div style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: '600' }}>Consents Done</div>
                </div>
              </div>
              <div style={{
                flex: '1',
                minWidth: '120px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #fbbf24',
                borderRadius: '14px',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('documents')}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  color: 'white'
                }}>
                  ⏳
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#92400e' }}>{pendingFormsCount}</div>
                  <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: '600' }}>Pending Forms</div>
                </div>
              </div>
            </div>
            
            {todayAppointments.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                borderRadius: '16px',
                border: '2px dashed #bbf7d0'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎉</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#166534', margin: '0 0 8px' }}>No Appointments Today</h3>
                <p style={{ color: '#15803d', fontSize: '0.95rem' }}>Enjoy your free day!</p>
              </div>
            ) : (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  padding: '14px 18px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>📋</div>
                    <div>
                      <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>
                        {todayAppointments.length} Appointment{todayAppointments.length !== 1 ? 's' : ''} Today
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        Sorted by appointment time
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        const allTodayIds = todayAppointments.map(a => a.id);
                        const allSelected = allTodayIds.every(id => selectedAppointments.includes(id));
                        if (allSelected) {
                          setSelectedAppointments(prev => prev.filter(id => !allTodayIds.includes(id)));
                        } else {
                          setSelectedAppointments(prev => [...new Set([...prev, ...allTodayIds])]);
                        }
                      }}
                      style={{
                        background: todayAppointments.every(a => selectedAppointments.includes(a.id)) 
                          ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' 
                          : 'white',
                        color: todayAppointments.every(a => selectedAppointments.includes(a.id)) ? 'white' : '#475569',
                        border: '1px solid #e2e8f0',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '100px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {todayAppointments.every(a => selectedAppointments.includes(a.id)) ? '☑ Deselect' : '☐ Select All'}
                    </button>
                    <button
                      onClick={async () => {
                        const selectedToday = todayAppointments.filter(a => selectedAppointments.includes(a.id));
                        if (selectedToday.length === 0) return;
                        if (!window.confirm(`Send reminder to ${selectedToday.length} selected?`)) return;
                        let sent = 0, failed = 0;
                        for (const apt of selectedToday) {
                          try {
                            await sendReminderEmail(
                              apt,
                              { phone: preparer.phone, email: preparer.email, address: preparer.address },
                              { name: company?.name || 'Tax Services', phone: company?.phone, email: company?.email, address: company?.address }
                            );
                            sent++;
                          } catch (err) {
                            failed++;
                          }
                        }
                        alert(`✅ Sent ${sent}${failed > 0 ? `, ${failed} failed` : ''}`);
                        setSelectedAppointments(prev => prev.filter(id => !selectedToday.map(a => a.id).includes(id)));
                      }}
                      disabled={selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length === 0}
                      style={{
                        background: selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length > 0 
                          ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                          : '#f1f5f9',
                        color: selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length > 0 ? 'white' : '#94a3b8',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length > 0 ? 'pointer' : 'not-allowed',
                        minWidth: '100px',
                        textAlign: 'center',
                        boxShadow: selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length > 0 
                          ? '0 2px 8px rgba(34, 197, 94, 0.3)' 
                          : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ✉️ Send ({selectedAppointments.filter(id => todayAppointments.some(a => a.id === id)).length})
                    </button>
                  </div>
                </div>
                {paginatedToday.map((apt, index) => {
                  const queueNumber = (todayPage - 1) * appointmentsPerPage + index + 1;
                  const isNextUp = index === 0 && todayPage === 1;
                  return (
                  <div key={apt.id} style={{ 
                    position: 'relative',
                    background: selectedAppointments.includes(apt.id) 
                      ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' 
                      : isNextUp 
                        ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '12px',
                    border: selectedAppointments.includes(apt.id) 
                      ? '2px solid #1976d2' 
                      : isNextUp 
                        ? '2px solid #22c55e'
                        : '1px solid #e2e8f0',
                    padding: '0',
                    marginBottom: '16px',
                    boxShadow: selectedAppointments.includes(apt.id) || isNextUp 
                      ? '0 4px 12px rgba(0,0,0,0.1)' 
                      : '0 2px 8px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease'
                  }}>
                    {/* Next Up Banner */}
                    {isNextUp && (
                      <div style={{
                        background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        padding: '6px 16px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '0.9rem' }}>🔔</span> Next Up
                      </div>
                    )}
                    {/* Split appointment badge */}
                    {apt.groupId && (
                      <div style={{
                        background: '#3b82f6',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        padding: '4px 16px',
                        textAlign: 'center'
                      }}>
                        Part of {apt.totalGroupClients}-client group booking
                      </div>
                    )}
                    
                    <div style={{
                      padding: '16px',
                      display: 'flex',
                      flexDirection: isSmallMobile ? 'column' : 'row',
                      gap: '16px',
                      alignItems: isSmallMobile ? 'stretch' : 'flex-start'
                    }}>
                      {/* Left Section: Checkbox, Queue Number, Time */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexShrink: 0
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedAppointments.includes(apt.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedAppointments(prev => 
                              prev.includes(apt.id) 
                                ? prev.filter(id => id !== apt.id)
                                : [...prev, apt.id]
                            );
                          }}
                          style={{ 
                            width: '18px', 
                            height: '18px', 
                            cursor: 'pointer',
                            accentColor: '#1976d2'
                          }}
                        />
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: isNextUp 
                            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '0.95rem',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }}>
                          #{queueNumber}
                        </div>
                        <div style={{
                          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          minWidth: '90px',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: '700', 
                            color: '#1e293b',
                            lineHeight: 1
                          }}>
                            {formatTimeSlots(apt)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Center Section: Client Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          marginBottom: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <h4 style={{ 
                            margin: 0, 
                            fontSize: '1.1rem', 
                            fontWeight: '700', 
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            👤 {apt.fullName}
                          </h4>
                          {getStatusBadge(apt.status)}
                        </div>
                        
                        <div style={{ 
                          display: 'grid',
                          gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(2, 1fr)',
                          gap: '6px 16px',
                          fontSize: '0.88rem',
                          color: '#475569',
                          marginBottom: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            overflow: 'hidden'
                          }}>
                            <span style={{ 
                              width: '24px', 
                              height: '24px', 
                              background: '#dbeafe', 
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              flexShrink: 0
                            }}>📧</span>
                            <span style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>{apt.email}</span>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px'
                          }}>
                            <span style={{ 
                              width: '24px', 
                              height: '24px', 
                              background: '#dcfce7', 
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              flexShrink: 0
                            }}>📞</span>
                            <span>{apt.phoneNumber}</span>
                          </div>
                          {apt.fillingType && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px'
                            }}>
                              <span style={{ 
                                width: '24px', 
                                height: '24px', 
                                background: '#fef3c7', 
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.85rem',
                                flexShrink: 0
                              }}>📄</span>
                              <span>{apt.fillingType}</span>
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px'
                          }}>
                            <span style={{ 
                              width: '24px', 
                              height: '24px', 
                              background: '#f3e8ff', 
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              flexShrink: 0
                            }}>👥</span>
                            <div style={{ fontWeight: '600' }}>{apt.numberOfClients || '1'} Client(s)</div>
                          </div>
                        </div>
                        
                        {/* Action Buttons for Today */}
                        {renderAppointmentActions(apt, true)}
                      </div>
                    </div>
                  </div>
                );
                })}
                <PaginationControls
                  currentPage={todayPage}
                  totalPages={totalTodayPages}
                  onPageChange={setTodayPage}
                  totalItems={todayAppointments.length}
                  itemsPerPage={appointmentsPerPage}
                />
              </div>
            )}
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div style={{ padding: '0' }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ 
                fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', 
                fontWeight: '700', 
                color: '#1e293b', 
                margin: '0 0 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                📅 Set Your Availability
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem', margin: 0 }}>Select a date and choose your available time slots</p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isTablet ? '1fr' : '1fr 1.5fr',
              gap: '24px'
            }}>
              {/* Calendar Side */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                padding: isMobile ? '16px' : '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <button 
                    onClick={() => navigateMonth(-1)}
                    aria-label="Previous month"
                    style={{
                      width: isMobile ? '44px' : '36px',
                      height: isMobile ? '44px' : '36px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                      cursor: 'pointer',
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: '#64748b',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ‹
                  </button>
                  <h3 style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: isMobile ? '1.1rem' : '1rem' }}>{getMonthName(currentMonth)}</h3>
                  <button 
                    onClick={() => navigateMonth(1)}
                    aria-label="Next month"
                    style={{
                      width: isMobile ? '44px' : '36px',
                      height: isMobile ? '44px' : '36px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                      cursor: 'pointer',
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: '#64748b',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ›
                  </button>
                </div>

                <div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, 1fr)', 
                    gap: isMobile ? '2px' : '4px', 
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    {(isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day, i) => (
                      <div key={i} style={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: '600', color: '#64748b', padding: isMobile ? '6px 0' : '8px 0' }}>{day}</div>
                    ))}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? '2px' : '4px' }}>
                    {generateCalendarDays().map((date, index) => (
                      <button
                        key={index}
                        onClick={() => handleDateClick(date)}
                        disabled={!date || isPastDate(date)}
                        style={{
                          aspectRatio: '1',
                          borderRadius: isMobile ? '10px' : '8px',
                          border: isMultiSelected(date) ? '2px solid #3b82f6' : '1px solid transparent',
                          background: isMultiSelected(date) 
                            ? 'linear-gradient(135deg, #3b82f6, #2563eb)' 
                            : isToday(date) 
                              ? '#eff6ff' 
                              : 'white',
                          color: isMultiSelected(date) ? 'white' : isPastDate(date) ? '#cbd5e1' : isToday(date) ? '#3b82f6' : '#1e293b',
                          cursor: !date || isPastDate(date) ? 'default' : 'pointer',
                          fontWeight: isToday(date) || isMultiSelected(date) ? '700' : '500',
                          fontSize: isMobile ? '0.8rem' : '0.85rem',
                          position: 'relative',
                          opacity: !date ? 0 : 1,
                          minHeight: isMobile ? '38px' : 'auto',
                          transition: 'all 0.15s ease',
                          boxShadow: isMultiSelected(date) ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                        }}
                      >
                        {date ? date.getDate() : ''}
                        {hasAvailability(date) && (
                          <span style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: isMultiSelected(date) ? 'white' : '#10b981'
                          }}></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#eff6ff', border: '2px solid #3b82f6' }}></span>
                    <span style={{ color: '#64748b' }}>Today</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10b981' }}></span>
                    <span style={{ color: '#64748b' }}>Has Availability</span>
                  </div>
                </div>
              </div>

              {/* Time Slots Side */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                {selectedDates.length > 0 ? (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ margin: '0', fontWeight: '600', color: '#1e293b', fontSize: '1.1rem' }}>
                          📅 {selectedDates.length} Date{selectedDates.length !== 1 ? 's' : ''} Selected
                        </h3>
                        <button
                          onClick={clearSelectedDates}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}
                        >
                          ✕ Clear All
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                        {selectedDates.map((date, idx) => (
                          <span
                            key={idx}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              background: '#dbeafe',
                              color: '#1d4ed8',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}
                          >
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            <button
                              onClick={() => handleDateClick(date)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                padding: '0',
                                fontSize: '1rem',
                                lineHeight: 1,
                                fontWeight: 'bold'
                              }}
                              title="Remove this date"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                        {selectedTimeSlots.length} time slot{selectedTimeSlots.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '10px' : '8px', marginBottom: '20px' }}>
                      <button onClick={selectAllSlots} style={{ padding: isMobile ? '12px 16px' : '8px 12px', minHeight: '44px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', cursor: 'pointer', fontSize: isMobile ? '0.9rem' : '0.85rem', color: '#475569', fontWeight: '500' }}>Select All</button>
                      <button onClick={selectMorningSlots} style={{ padding: isMobile ? '12px 16px' : '8px 12px', minHeight: '44px', borderRadius: '10px', border: '1px solid #fef3c7', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', cursor: 'pointer', fontSize: isMobile ? '0.9rem' : '0.85rem', color: '#92400e', fontWeight: '500' }}>🌅 Morning</button>
                      <button onClick={selectAfternoonSlots} style={{ padding: isMobile ? '12px 16px' : '8px 12px', minHeight: '44px', borderRadius: '10px', border: '1px solid #fef9c3', background: 'linear-gradient(135deg, #fefce8, #fef9c3)', cursor: 'pointer', fontSize: isMobile ? '0.9rem' : '0.85rem', color: '#854d0e', fontWeight: '500' }}>☀️ Afternoon</button>
                      <button onClick={selectEveningSlots} style={{ padding: isMobile ? '12px 16px' : '8px 12px', minHeight: '44px', borderRadius: '10px', border: '1px solid #e0e7ff', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', cursor: 'pointer', fontSize: isMobile ? '0.9rem' : '0.85rem', color: '#4338ca', fontWeight: '500' }}>🌙 Evening</button>
                      <button onClick={clearAllSlots} style={{ padding: isMobile ? '12px 16px' : '8px 12px', minHeight: '44px', borderRadius: '10px', border: '1px solid #fecaca', background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', cursor: 'pointer', fontSize: isMobile ? '0.9rem' : '0.85rem', color: '#dc2626', fontWeight: '500' }}>✕ Clear</button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Morning: 6 AM - 12 PM */}
                      <div>
                        <h4 style={{ margin: '0 0 12px', fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>🌅 Morning (6 AM - 12 PM)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(85px, 1fr))', gap: isMobile ? '10px' : '8px' }}>
                          {timeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 6 && hour < 12;
                          }).map(slot => (
                            <button
                              key={slot}
                              onClick={() => toggleTimeSlot(slot)}
                              style={{
                                padding: isMobile ? '14px 8px' : '10px 8px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                border: selectedTimeSlots.includes(slot) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                background: selectedTimeSlots.includes(slot) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'white',
                                color: selectedTimeSlots.includes(slot) ? 'white' : '#475569',
                                fontWeight: selectedTimeSlots.includes(slot) ? '600' : '500',
                                cursor: 'pointer',
                                fontSize: isMobile ? '0.9rem' : '0.85rem',
                                transition: 'all 0.15s ease',
                                boxShadow: selectedTimeSlots.includes(slot) ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                              }}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Afternoon: 12 PM - 5 PM */}
                      <div>
                        <h4 style={{ margin: '0 0 12px', fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>☀️ Afternoon (12 PM - 5 PM)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(85px, 1fr))', gap: isMobile ? '10px' : '8px' }}>
                          {timeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 12 && hour < 17;
                          }).map(slot => (
                            <button
                              key={slot}
                              onClick={() => toggleTimeSlot(slot)}
                              style={{
                                padding: isMobile ? '14px 8px' : '10px 8px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                border: selectedTimeSlots.includes(slot) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                background: selectedTimeSlots.includes(slot) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'white',
                                color: selectedTimeSlots.includes(slot) ? 'white' : '#475569',
                                fontWeight: selectedTimeSlots.includes(slot) ? '600' : '500',
                                cursor: 'pointer',
                                fontSize: isMobile ? '0.9rem' : '0.85rem',
                                transition: 'all 0.15s ease',
                                boxShadow: selectedTimeSlots.includes(slot) ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                              }}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Evening: 5 PM - 11 PM */}
                      <div>
                        <h4 style={{ margin: '0 0 12px', fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>🌙 Evening (5 PM - 11 PM)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(85px, 1fr))', gap: isMobile ? '10px' : '8px' }}>
                          {timeSlots.filter(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            return hour >= 17 && hour <= 23;
                          }).map(slot => (
                            <button
                              key={slot}
                              onClick={() => toggleTimeSlot(slot)}
                              style={{
                                padding: isMobile ? '14px 8px' : '10px 8px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                border: selectedTimeSlots.includes(slot) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                background: selectedTimeSlots.includes(slot) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'white',
                                color: selectedTimeSlots.includes(slot) ? 'white' : '#475569',
                                fontWeight: selectedTimeSlots.includes(slot) ? '600' : '500',
                                cursor: 'pointer',
                                fontSize: isMobile ? '0.9rem' : '0.85rem',
                                transition: 'all 0.15s ease',
                                boxShadow: selectedTimeSlots.includes(slot) ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                              }}
                            >
                              {convert24to12Hour(slot)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                      <button 
                        onClick={handleSaveAvailability}
                        disabled={saving || selectedTimeSlots.length === 0}
                        style={{
                          width: '100%',
                          padding: '16px 24px',
                          borderRadius: '12px',
                          border: 'none',
                          background: selectedTimeSlots.length === 0 ? '#94a3b8' : 'linear-gradient(135deg, #10b981, #059669)',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          cursor: saving || selectedTimeSlots.length === 0 ? 'not-allowed' : 'pointer',
                          opacity: saving ? 0.7 : 1,
                          boxShadow: selectedTimeSlots.length > 0 ? '0 4px 15px rgba(16, 185, 129, 0.4)' : 'none',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {saving ? '⏳ Saving...' : `💾 SAVE AVAILABILITY (${selectedDates.length} Date${selectedDates.length !== 1 ? 's' : ''}, ${selectedTimeSlots.length} Slot${selectedTimeSlots.length !== 1 ? 's' : ''})`}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '12px',
                    border: '2px dashed #e2e8f0'
                  }}>
                    <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📆</div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: '0 0 8px' }}>Select Dates</h3>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: '300px', margin: '0 auto' }}>Click on multiple dates in the calendar to set your available time slots. You can select as many dates as you need.</p>
                  </div>
                )}
              </div>
            </div>

            {/* My Current Availability Section */}
            <div style={{
              marginTop: '24px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                padding: '12px 16px',
                color: 'white'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📅 My Current Availability
                </h3>
              </div>
              
              <div style={{ padding: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                {(() => {
                  // Get unique dates, preferring name-based keys over id-based keys
                  const uniqueDateKeys = {};
                  Object.keys(availability).forEach(key => {
                    const datePart = key.includes('_') ? key.split('_').pop() : key;
                    const dateObj = new Date(datePart + 'T00:00:00');
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const slots = getDisplaySlots(key);
                    
                    if (dateObj >= today && slots?.length > 0) {
                      // Prefer preparer.name keys over preparer.id keys
                      if (!uniqueDateKeys[datePart] || key.startsWith(preparer.name + '_')) {
                        uniqueDateKeys[datePart] = key;
                      }
                    }
                  });
                  
                  const filteredKeys = Object.values(uniqueDateKeys).sort((a, b) => {
                    const dateA = a.includes('_') ? a.split('_').pop() : a;
                    const dateB = b.includes('_') ? b.split('_').pop() : b;
                    return dateA.localeCompare(dateB);
                  });
                  
                  return filteredKeys.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 15px', color: '#64748b' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📆</div>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>No upcoming availability set.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredKeys.map(key => {
                        const slots = getDisplaySlots(key);
                        const datePart = key.includes('_') ? key.split('_').pop() : key;
                        const dateObj = new Date(datePart + 'T00:00:00');
                        const formattedDate = dateObj.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        });
                        const isEdited = hasEditedSlots(key);
                        return (
                          <div 
                            key={key}
                            style={{
                              padding: '10px 12px',
                              background: isEdited ? '#fef3c7' : '#f8fafc',
                              borderRadius: '8px',
                              border: isEdited ? '2px solid #f59e0b' : '1px solid #e2e8f0'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                                {formattedDate}
                                {isEdited && <span style={{ color: '#f59e0b', marginLeft: '6px', fontSize: '0.75rem' }}>• Unsaved</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {isEdited && (
                                  <button
                                    onClick={() => handleSaveEditedAvailability(key)}
                                    style={{
                                      padding: '4px 10px',
                                      background: 'linear-gradient(135deg, #10b981, #059669)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '5px',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    💾 Save
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteAvailability(key)}
                                  style={{
                                    padding: '4px 10px',
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  🗑️ Delete All
                                </button>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {slots.sort().map(slot => (
                                <span 
                                  key={slot}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    padding: '2px 8px',
                                    background: '#dbeafe',
                                    color: '#1d4ed8',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  {convert24to12Hour(slot)}
                                  <button
                                    onClick={() => handleDeleteTimeSlot(key, slot)}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#dc2626',
                                      cursor: 'pointer',
                                      padding: '0',
                                      fontSize: '0.85rem',
                                      lineHeight: 1,
                                      marginLeft: '1px'
                                    }}
                                    title="Remove this slot"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div style={{ padding: '0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ 
                fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', 
                fontWeight: '700', 
                color: '#1e293b', 
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>👤 My Profile</h2>
              <button 
                onClick={openEditProfile}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                ✏️ Edit Profile
              </button>
            </div>
            
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '24px',
              alignItems: isMobile ? 'center' : 'flex-start'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {preparer.photo ? (
                  <img src={preparer.photo} alt={preparer.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '3rem' }}>👤</span>
                )}
              </div>
              
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>{preparer.name}</h3>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.9rem' }}>@{preparer.username}</p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {preparer.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                      <span>✉️</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{preparer.email}</span>
                    </div>
                  )}
                  {preparer.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                      <span>📞</span>
                      <span>{preparer.phone}</span>
                    </div>
                  )}
                  {preparer.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                      <span>📍</span>
                      <span>{preparer.address}</span>
                    </div>
                  )}
                </div>

                {preparer.bio && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 8px', fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>Bio</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>{preparer.bio}</p>
                  </div>
                )}
              </div>
            </div>

            {company && (
              <div style={{
                marginTop: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <h3 style={{ margin: '0 0 16px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>🏢 My Company</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  {company.logo && (
                    <img src={company.logo} alt={company.name} style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 8px', fontWeight: '600', color: '#1e293b' }}>{company.name}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9rem', color: '#64748b' }}>
                      {company.email && <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>✉️ {company.email}</p>}
                      {company.phone && <p style={{ margin: 0 }}>📞 {company.phone}</p>}
                      {company.address && <p style={{ margin: 0 }}>📍 {company.address}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div style={{ padding: '0' }}>
            {/* Stats Summary Bar */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                flex: '1',
                minWidth: '140px',
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                border: '1px solid #86efac',
                borderRadius: '14px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  color: 'white',
                  fontWeight: '700'
                }}>
                  ✅
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#166534' }}>{completedFormsCount}</div>
                  <div style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: '600' }}>Completed</div>
                </div>
              </div>
              <div style={{
                flex: '1',
                minWidth: '140px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #fbbf24',
                borderRadius: '14px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  color: 'white',
                  fontWeight: '700'
                }}>
                  ⏳
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#92400e' }}>{pendingFormsCount}</div>
                  <div style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: '600' }}>Pending</div>
                </div>
              </div>
              <div style={{
                flex: '1',
                minWidth: '140px',
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '14px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  color: 'white',
                  fontWeight: '700'
                }}>
                  📄
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1e40af' }}>{completedFormsCount + pendingFormsCount}</div>
                  <div style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: '600' }}>Total Forms</div>
                </div>
              </div>
            </div>

            {/* Create Form Button */}
            <button
              onClick={() => {
                setEditingTemplate(null);
                setShowDocumentBuilder(true);
              }}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1.05rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '24px',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.35)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.45)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 92, 246, 0.35)'; }}
            >
              ➕ Create New Form
            </button>

            {/* Saved Form Templates Section */}
            {documentTemplates.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '42px',
                      height: '42px',
                      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      boxShadow: '0 3px 10px rgba(99, 102, 241, 0.3)'
                    }}>📑</div>
                    <div>
                      <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: '700' }}>Your Form Templates</h3>
                      <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '0.8rem' }}>Send to clients to fill &amp; sign</p>
                    </div>
                  </div>
                  <span style={{
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '700'
                  }}>
                    {documentTemplates.length} Template{documentTemplates.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {documentTemplates.map(template => (
                    <div key={template.id} style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'box-shadow 0.2s'
                    }}>
                      {/* Template Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                        <div style={{
                          width: '44px',
                          height: '44px',
                          background: '#f1f5f9',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.3rem',
                          flexShrink: 0
                        }}>📄</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {template.name || 'Untitled Form'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '2px' }}>
                            {template.fields?.length || 0} fields · Created {template.createdAt ? new Date(template.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openSendDocumentModal(template)}
                          style={{
                            padding: '8px 14px',
                            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            minHeight: '38px'
                          }}
                        >
                          📤 Send
                        </button>
                        <button
                          onClick={() => {
                            const formUrl = `${window.location.origin}/document?template=${template.id}`;
                            navigator.clipboard.writeText(formUrl);
                            alert('✅ Form link copied!\n\nShare this link with your client to fill out this form.\n\nURL: ' + formUrl);
                          }}
                          style={{
                            padding: '8px 14px',
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            minHeight: '38px'
                          }}
                        >
                          🔗 Copy Link
                        </button>
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            setShowDocumentBuilder(true);
                          }}
                          style={{
                            padding: '8px 14px',
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            minHeight: '38px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          style={{
                            padding: '8px 14px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            minHeight: '38px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Submissions Toggle & List */}
                      {(() => {
                        const templateRequests = documentRequests.filter(r => r.templateId === template.id);
                        const templateSigned = signedDocuments.filter(s => s.templateId === template.id);
                        const pendingCount = templateRequests.filter(r => r.status === 'pending').length;
                        const completedCount = templateRequests.filter(r => r.status === 'signed').length;
                        const totalCount = templateRequests.length;
                        const isExpanded = expandedTemplateSubmissions[template.id];

                        return totalCount > 0 ? (
                          <div style={{ width: '100%', marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                            <button
                              onClick={() => setExpandedTemplateSubmissions(prev => ({ ...prev, [template.id]: !prev[template.id] }))}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '6px 4px',
                                borderRadius: '8px',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                              onMouseOut={e => e.currentTarget.style.background = 'none'}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569' }}>
                                  📋 Client Submissions
                                </span>
                                <span style={{
                                  background: completedCount > 0 ? '#dcfce7' : '#fef3c7',
                                  color: completedCount > 0 ? '#15803d' : '#92400e',
                                  padding: '2px 10px',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}>
                                  {completedCount} completed
                                </span>
                                {pendingCount > 0 && (
                                  <span style={{
                                    background: '#fef3c7',
                                    color: '#92400e',
                                    padding: '2px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: '700'
                                  }}>
                                    {pendingCount} pending
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: '0.9rem', color: '#94a3b8', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                ▼
                              </span>
                            </button>

                            {isExpanded && (
                              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {/* Pending Submissions */}
                                {templateRequests.filter(r => r.status === 'pending').length > 0 && (
                                  <div style={{ marginBottom: '6px' }}>
                                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#92400e', marginBottom: '6px', padding: '0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      ⏳ Pending ({templateRequests.filter(r => r.status === 'pending').length})
                                    </div>
                                    {templateRequests.filter(r => r.status === 'pending').map(request => {
                                      const templateFields = request.template?.fields || request.templateData?.fields || template.fields || [];
                                      const clientFields = templateFields.filter(f => f.filledBy !== 'preparer');
                                      const preparerFields = templateFields.filter(f => f.filledBy === 'preparer');
                                      return (
                                        <div key={request.id} onClick={() => {
                                          const fields = request.template?.fields || request.templateData?.fields || template.fields || [];
                                          const initialValues = {};
                                          fields.forEach(f => { initialValues[f.id] = f.type === 'checkbox' ? false : ''; });
                                          setPendingFormValues(initialValues);
                                          setEditingPendingRequest({ ...request, _templateFields: fields, _parentTemplate: template });
                                        }} style={{
                                          background: '#fffbeb',
                                          border: '1px solid #fde68a',
                                          borderRadius: '10px',
                                          padding: '12px 14px',
                                          marginBottom: '6px',
                                          cursor: 'pointer',
                                          transition: 'box-shadow 0.2s, border-color 0.2s'
                                        }}
                                        onMouseOver={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(245,158,11,0.15)'; e.currentTarget.style.borderColor = '#f59e0b'; }}
                                        onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#fde68a'; }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <span style={{ fontSize: '1rem' }}>⏳</span>
                                              <div>
                                                <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1e293b' }}>
                                                  {request.clientName || 'Waiting for client'}
                                                </div>
                                                <div style={{ fontSize: '0.73rem', color: '#64748b' }}>
                                                  {request.clientEmail || 'No email'} · Sent {new Date(request.createdAt?.toDate?.() || request.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </div>
                                              </div>
                                            </div>
                                            <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700', background: '#fef3c7', color: '#92400e' }}>
                                              Pending
                                            </span>
                                          </div>
                                          {/* Fields to complete */}
                                          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px 12px' }}>
                                            <div style={{ fontSize: '0.73rem', fontWeight: '700', color: '#9a3412', marginBottom: '6px' }}>
                                              📝 Fields to complete:
                                            </div>
                                            {clientFields.length > 0 && (
                                              <div style={{ marginBottom: preparerFields.length > 0 ? '6px' : 0 }}>
                                                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>👤 Client fills:</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                  {clientFields.map(f => (
                                                    <span key={f.id} style={{ fontSize: '0.7rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '6px', fontWeight: '500' }}>
                                                      {f.label}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            {preparerFields.length > 0 && (
                                              <div>
                                                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>📋 Preparer fills:</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                  {preparerFields.map(f => (
                                                    <span key={f.id} style={{ fontSize: '0.7rem', background: '#f5f3ff', color: '#6d28d9', padding: '2px 8px', borderRadius: '6px', fontWeight: '500' }}>
                                                      {f.label}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Completed Submissions */}
                                {templateRequests.filter(r => r.status === 'signed').length > 0 && (
                                  <div>
                                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#15803d', marginBottom: '6px', padding: '0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      ✅ Completed ({templateRequests.filter(r => r.status === 'signed').length})
                                    </div>
                                    {templateRequests.filter(r => r.status === 'signed').map(request => {
                                      const signedDoc = templateSigned.find(s => s.requestId === request.id);
                                      return (
                                        <div key={request.id} onClick={() => {
                                          const fields = request.template?.fields || request.templateData?.fields || template.fields || [];
                                          const pdfData = signedDoc?.pdfData || request.template?.pdfData || template.pdfData;
                                          const fieldVals = signedDoc?.fieldValues || {};
                                          const sigs = signedDoc?.signatures || {};
                                          setViewingCompletedDoc({
                                            clientName: request.clientName || signedDoc?.clientName || 'Client',
                                            clientEmail: request.clientEmail || signedDoc?.clientEmail || '',
                                            signedAt: signedDoc?.signedAt || request.signedAt,
                                            templateName: template.name || signedDoc?.templateName || 'Document',
                                            pdfData,
                                            fields,
                                            fieldValues: fieldVals,
                                            signatures: sigs
                                          });
                                          setViewerCurrentPage(1);
                                          setViewerNumPages(null);
                                        }} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          background: '#f0fdf4',
                                          border: '1px solid #bbf7d0',
                                          borderRadius: '10px',
                                          padding: '10px 14px',
                                          marginBottom: '6px',
                                          cursor: 'pointer',
                                          transition: 'box-shadow 0.2s, border-color 0.2s'
                                        }}
                                        onMouseOver={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(34,197,94,0.15)'; e.currentTarget.style.borderColor = '#22c55e'; }}
                                        onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                            <span style={{ fontSize: '1rem' }}>✅</span>
                                            <div style={{ minWidth: 0 }}>
                                              <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {request.clientName || signedDoc?.clientName || 'Client'}
                                              </div>
                                              <div style={{ fontSize: '0.73rem', color: '#64748b' }}>
                                                {request.clientEmail || signedDoc?.clientEmail || ''}
                                                {(signedDoc?.signedAt || request.signedAt) ? ` · Signed ${new Date(signedDoc?.signedAt || request.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                                              </div>
                                            </div>
                                          </div>
                                          <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700', background: '#dcfce7', color: '#15803d', flexShrink: 0 }}>
                                            Completed
                                          </span>
                                          <span style={{ fontSize: '0.8rem', color: '#22c55e', marginLeft: '6px', flexShrink: 0 }}>👁️ View</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* IRS Waiver Forms Section */}
            <div style={{ marginBottom: '40px' }}>
              {/* Header with gradient */}
              <div style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                borderRadius: '16px 16px 0 0',
                padding: 'clamp(16px, 4vw, 25px) clamp(16px, 4vw, 30px)',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    background: 'rgba(255,255,255,0.2)', 
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    📝
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 style={{ margin: 0, fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: '700' }}>IRS Waiver Forms</h2>
                    <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>Send IRS Notice 2014-7 waiver form with client info & e-signature</p>
                  </div>
                </div>
              </div>
              
              {/* Form Card Content */}
              <div style={{ 
                background: 'white',
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                borderRadius: '0 0 16px 16px',
                padding: '25px 30px'
              }}>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${company?.id || ''}&preparer=${preparer?.id || ''}`;
                      navigator.clipboard.writeText(formUrl);
                      alert('✅ IRS Waiver Form link copied!\n\nShare this link with your client to complete tax documents.\n\nURL: ' + formUrl);
                    }}
                    title="Copy Form Link"
                    style={{ 
                      width: '52px',
                      height: '52px',
                      padding: '0', 
                      background: 'linear-gradient(135deg, #10b981, #059669)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '14px', 
                      cursor: 'pointer', 
                      fontSize: '1.4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${company?.id || ''}&preparer=${preparer?.id || ''}`;
                      window.open(formUrl, '_blank');
                    }}
                    title="Preview Form"
                    style={{ 
                      width: '52px',
                      height: '52px',
                      padding: '0', 
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '14px', 
                      cursor: 'pointer', 
                      fontSize: '1.4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    👁️
                  </button>
                  <button
                    onClick={() => setShowSendConsentModal(true)}
                    title="Email Form to Client"
                    style={{ 
                      width: '52px',
                      height: '52px',
                      padding: '0', 
                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '14px', 
                      cursor: 'pointer', 
                      fontSize: '1.4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    ✉️
                  </button>
                </div>
              </div>
            </div>

            {/* Pending Verification Section - IRS Waiver Submissions needing preparer review */}
            <div style={{ marginBottom: '30px' }}>
              {/* Professional Section Header */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '14px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.3rem',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                  }}>
                    🔍
                  </div>
                  <div>
                    <h3 style={{ 
                      margin: 0, 
                      color: '#1e293b', 
                      fontSize: '1.2rem', 
                      fontWeight: '700',
                      letterSpacing: '-0.01em'
                    }}>Pending Verification</h3>
                    <p style={{ 
                      margin: '2px 0 0 0', 
                      color: '#64748b', 
                      fontSize: '0.85rem' 
                    }}>Awaiting your review and approval</p>
                  </div>
                </div>
                <div style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', 
                  color: 'white', 
                  padding: '8px 18px', 
                  borderRadius: '25px', 
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '0.85rem' }}>⏳</span>
                  {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').length} Pending
                </div>
              </div>
              
              {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').length === 0 ? (
                <div style={{ 
                  background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)', 
                  padding: '30px', 
                  borderRadius: '16px', 
                  textAlign: 'center', 
                  color: '#7c3aed', 
                  border: '2px dashed #c4b5fd'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>No pending submissions. When clients submit the IRS Waiver form, they'll appear here for your verification.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Selection Actions Bar */}
                  {selectedPendingForms.length > 0 && (
                    <div style={{ 
                      padding: '14px 18px', 
                      background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', 
                      border: '2px solid #8b5cf6', 
                      borderRadius: '12px', 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: '12px' 
                    }}>
                      <span style={{ color: '#7c3aed', fontSize: '0.95rem', fontWeight: '600' }}>☑️ {selectedPendingForms.length} selected</span>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Verify ${selectedPendingForms.length} submission(s)?`)) {
                              for (const id of selectedPendingForms) {
                                try {
                                  await verifyIRSWaiverSubmission(id, preparer?.id, preparer?.name);
                                } catch (e) { console.error(e); }
                              }
                              setSelectedPendingForms([]);
                              alert('✅ Submissions verified!');
                            }
                          }}
                          style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}
                        >
                          ✓ Verify All
                        </button>
                        <button onClick={() => setSelectedPendingForms([])} style={{ padding: '10px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}>✕ Clear</button>
                      </div>
                    </div>
                  )}
                  
                  {irsWaiverSubmissions.filter(s => s.status === 'pending_verification').map(submission => (
                    <div key={submission.id} style={{ 
                      background: 'white', 
                      border: selectedPendingForms.includes(submission.id) ? '2px solid #8b5cf6' : '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '20px', 
                      padding: '0',
                      boxShadow: selectedPendingForms.includes(submission.id) 
                        ? '0 8px 32px rgba(139, 92, 246, 0.25)' 
                        : '0 4px 20px rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: selectedPendingForms.includes(submission.id) ? 'scale(1.01)' : 'scale(1)'
                    }}>
                      {/* Professional Card Header Banner */}
                      <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                        padding: '20px 24px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {/* Decorative Pattern Overlay */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          opacity: 0.5
                        }} />
                        {/* Glassmorphism accent */}
                        <div style={{
                          position: 'absolute',
                          top: '-50%',
                          right: '-20%',
                          width: '200px',
                          height: '200px',
                          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                          borderRadius: '50%'
                        }} />
                        
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '12px',
                          position: 'relative',
                          zIndex: 1
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{
                              width: '44px',
                              height: '44px',
                              background: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(10px)',
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(255,255,255,0.3)'
                            }}>
                              <input
                                type="checkbox"
                                checked={selectedPendingForms.includes(submission.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedPendingForms(prev => [...prev, submission.id]);
                                  else setSelectedPendingForms(prev => prev.filter(id => id !== submission.id));
                                }}
                                style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#fff' }}
                              />
                            </div>
                            <div>
                              <h4 style={{ 
                                margin: 0, 
                                color: 'white', 
                                fontWeight: '700', 
                                fontSize: '1.15rem',
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                letterSpacing: '-0.01em'
                              }}>
                                {submission.taxpayerInfo?.name || 'Unknown Client'}
                              </h4>
                              <p style={{ 
                                margin: '4px 0 0 0', 
                                color: 'rgba(255,255,255,0.9)', 
                                fontSize: '0.9rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <span style={{ opacity: 0.8 }}>📅</span> Tax Year {submission.taxYear}
                              </p>
                            </div>
                          </div>
                          <div style={{ 
                            background: 'rgba(255,255,255,0.15)', 
                            backdropFilter: 'blur(10px)',
                            color: 'white', 
                            padding: '8px 16px', 
                            borderRadius: '25px', 
                            fontSize: '0.85rem', 
                            fontWeight: '600',
                            border: '1px solid rgba(255,255,255,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                          }}>
                            <span style={{ 
                              width: '8px', 
                              height: '8px', 
                              background: '#fbbf24', 
                              borderRadius: '50%',
                              animation: 'pulse 2s infinite'
                            }} />
                            Pending Review
                          </div>
                        </div>
                      </div>
                      
                      {/* Card Body */}
                      <div style={{ padding: '18px 20px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>📧</span> {submission.taxpayerInfo?.email || 'N/A'}
                            </p>
                            {submission.taxpayerInfo?.phone && (
                              <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>📞</span> {submission.taxpayerInfo.phone}
                              </p>
                            )}
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
                              Submitted: {(() => {
                                const dateValue = submission.submittedAt;
                                if (!dateValue) return 'N/A';
                                const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
                                if (isNaN(date.getTime())) return 'N/A';
                                return date.toLocaleDateString();
                              })()}
                            </p>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
                            {submission.photoIDs?.consumer?.startsWith('data:') && (
                              <span style={{ background: '#dcfce7', color: '#166534', padding: '5px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '500' }}>📷 ID Uploaded</span>
                            )}
                            {submission.signature?.hasSignature && (
                              <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '5px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '500' }}>✍️ Signed</span>
                            )}
                            {submission.consents?.engagementAccepted && (
                              <span style={{ background: '#fef3c7', color: '#92400e', padding: '5px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '500' }}>✓ Terms</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                          gap: '10px',
                          paddingTop: '16px',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <button
                            onClick={() => setViewingWaiverSubmission(submission)}
                            style={{ 
                              padding: '12px 16px', 
                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '10px', 
                              cursor: 'pointer', 
                              fontWeight: '600', 
                              fontSize: '0.9rem',
                              minHeight: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={async () => await downloadIRSWaiverPDF(submission, company, preparer)}
                            style={{ 
                              padding: '12px 16px', 
                              background: 'linear-gradient(135deg, #10b981, #059669)', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '10px', 
                              cursor: 'pointer', 
                              fontWeight: '600', 
                              fontSize: '0.9rem',
                              minHeight: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            📥 PDF
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Verify and approve this submission?')) {
                                try {
                                  await verifyIRSWaiverSubmission(submission.id, preparer?.id, preparer?.name);
                                  alert('✅ Submission verified!');
                                } catch (error) {
                                  alert('Failed to verify submission');
                                }
                              }
                            }}
                            style={{ 
                              padding: '12px 16px', 
                              background: 'linear-gradient(135deg, #22c55e, #16a34a)', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '10px', 
                              cursor: 'pointer', 
                              fontWeight: '700', 
                              fontSize: '0.9rem',
                              minHeight: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            ✓ Verify
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Delete this submission? This cannot be undone.')) {
                                try {
                                  await deleteIRSWaiverSubmission(submission.id);
                                } catch (error) {
                                  alert('Failed to delete');
                                }
                              }
                            }}
                            style={{ 
                              padding: '12px 16px', 
                              background: '#fee2e2', 
                              color: '#dc2626', 
                              border: 'none', 
                              borderRadius: '10px', 
                              cursor: 'pointer', 
                              fontWeight: '600', 
                              fontSize: '0.9rem',
                              minHeight: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed Forms Section */}
            <div style={{ marginBottom: '30px' }}>
              {/* Professional Section Header */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '14px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.3rem',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                  }}>
                    ✅
                  </div>
                  <div>
                    <h3 style={{ 
                      margin: 0, 
                      color: '#1e293b', 
                      fontSize: '1.2rem', 
                      fontWeight: '700',
                      letterSpacing: '-0.01em'
                    }}>Completed Forms</h3>
                    <p style={{ 
                      margin: '2px 0 0 0', 
                      color: '#64748b', 
                      fontSize: '0.85rem' 
                    }}>Verified and ready for download</p>
                  </div>
                </div>
                <div style={{ 
                  background: 'linear-gradient(135deg, #10b981, #059669)', 
                  color: 'white', 
                  padding: '8px 18px', 
                  borderRadius: '25px', 
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '0.85rem' }}>📄</span>
                  {irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').length + signedConsents.length} Forms
                </div>
              </div>
              
              {/* Search and Filter Section */}
              <div style={{ 
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                borderRadius: '16px',
                padding: isMobile ? '16px' : '20px',
                marginBottom: '16px',
                border: '1px solid #86efac',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto auto auto', gap: '12px', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>🔍 Search by Name</label>
                    <input
                      type="text"
                      placeholder="Client name..."
                      value={completedSearchName}
                      onChange={(e) => setCompletedSearchName(e.target.value)}
                      style={{ width: '100%', padding: '12px 14px', border: '2px solid #86efac', borderRadius: '10px', fontSize: '1rem', background: 'white', minHeight: '44px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>📅 From</label>
                    <input type="date" value={completedSearchDateFrom} onChange={(e) => setCompletedSearchDateFrom(e.target.value)} style={{ width: '100%', padding: '12px', border: '2px solid #86efac', borderRadius: '10px', fontSize: '1rem', background: 'white', minHeight: '44px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: '#166534', marginBottom: '6px', fontWeight: '600' }}>📅 To</label>
                    <input type="date" value={completedSearchDateTo} onChange={(e) => setCompletedSearchDateTo(e.target.value)} style={{ width: '100%', padding: '12px', border: '2px solid #86efac', borderRadius: '10px', fontSize: '1rem', background: 'white', minHeight: '44px', boxSizing: 'border-box' }} />
                  </div>
                  {(completedSearchName || completedSearchDateFrom || completedSearchDateTo) && (
                    <button onClick={() => { setCompletedSearchName(''); setCompletedSearchDateFrom(''); setCompletedSearchDateTo(''); }} style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}>✕ Clear</button>
                  )}
                </div>
              </div>

              {/* Selection Actions Bar */}
              {selectedCompletedForms.length > 0 && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '14px 18px', 
                  background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)', 
                  border: '2px solid #3b82f6', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  gap: '12px' 
                }}>
                  <span style={{ color: '#1d4ed8', fontSize: '0.95rem', fontWeight: '700' }}>☑️ {selectedCompletedForms.length} selected</span>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        // Combine both sources
                        const allCompleted = [
                          ...signedConsents,
                          ...irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed')
                        ];
                        const selectedSubs = allCompleted.filter(s => selectedCompletedForms.includes(s.id));
                        if (selectedSubs.length === 1) {
                          await downloadIRSWaiverPDF(selectedSubs[0], company, preparer);
                          setSelectedCompletedForms([]);
                        } else if (selectedSubs.length > 1) {
                          if (!window.confirm(`Download ${selectedSubs.length} forms as ZIP?`)) return;
                          const JSZip = (await import('jszip')).default;
                          const { saveAs } = await import('file-saver');
                          const zip = new JSZip();
                          for (const sub of selectedSubs) {
                            try {
                              const pdfBytes = await generateIRSWaiverPDF(sub, company, preparer);
                              const name = (sub.taxpayerInfo?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
                              zip.file(`IRS_Waiver_${name}_TY${sub.taxYear || 'Unknown'}.pdf`, pdfBytes);
                            } catch (e) { console.error(e); }
                          }
                          const blob = await zip.generateAsync({ type: 'blob' });
                          saveAs(blob, `Forms_${selectedSubs.length}_files.zip`);
                          setSelectedCompletedForms([]);
                        }
                      }}
                      style={{ padding: '12px 18px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}
                    >
                      📥 {selectedCompletedForms.length === 1 ? 'Download PDF' : `Download ${selectedCompletedForms.length} as ZIP`}
                    </button>
                    <button onClick={() => setSelectedCompletedForms([])} style={{ padding: '12px 18px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}>✕ Clear</button>
                  </div>
                </div>
              )}

              {/* Combined list of completed forms */}
              {(() => {
                const allCompletedForms = [
                  ...signedConsents.map(c => ({ ...c, source: 'consent' })),
                  ...irsWaiverSubmissions.filter(s => s.status === 'verified' || s.status === 'completed').map(s => ({ ...s, source: 'waiver' }))
                ].filter(item => {
                  const name = item.taxpayerInfo?.name || item.taxpayerName || '';
                  if (completedSearchName && !name.toLowerCase().includes(completedSearchName.toLowerCase())) return false;
                  const itemDate = new Date(item.signedAt || item.submittedAt || item.createdAt);
                  if (completedSearchDateFrom && itemDate < new Date(completedSearchDateFrom)) return false;
                  if (completedSearchDateTo) { const to = new Date(completedSearchDateTo); to.setHours(23,59,59,999); if (itemDate > to) return false; }
                  return true;
                }).sort((a, b) => {
                  const dateA = new Date(a.signedAt || a.submittedAt || a.createdAt);
                  const dateB = new Date(b.signedAt || b.submittedAt || b.createdAt);
                  return dateB - dateA; // newest first
                });

                if (allCompletedForms.length === 0) {
                  return (
                    <div style={{ 
                      background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', 
                      padding: '40px 30px', 
                      borderRadius: '16px', 
                      textAlign: 'center', 
                      color: '#166534', 
                      border: '2px dashed #86efac'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📄</div>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>No completed forms yet. Verify pending submissions to see them here.</p>
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Select All */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '10px 14px',
                      background: '#f8fafc',
                      borderRadius: '10px'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedCompletedForms.length === allCompletedForms.length && allCompletedForms.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedCompletedForms(allCompletedForms.map(f => f.id));
                          else setSelectedCompletedForms([]);
                        }}
                        style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#22c55e' }}
                      />
                      <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>
                        Select All ({allCompletedForms.length})
                      </span>
                    </div>
                    
                    {allCompletedForms.map(item => (
                      <div key={item.id} style={{ 
                        background: selectedCompletedForms.includes(item.id) ? 'linear-gradient(135deg, #f0fdf4, #ecfdf5)' : 'white', 
                        border: selectedCompletedForms.includes(item.id) ? '2px solid #22c55e' : '1px solid rgba(0,0,0,0.08)', 
                        borderRadius: '16px', 
                        padding: '0',
                        boxShadow: selectedCompletedForms.includes(item.id) 
                          ? '0 8px 25px rgba(34, 197, 94, 0.15)' 
                          : '0 2px 12px rgba(0,0,0,0.04)',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: selectedCompletedForms.includes(item.id) ? 'translateY(-2px)' : 'translateY(0)'
                      }}>
                        {/* Professional Top Banner */}
                        <div style={{
                          background: item.source === 'waiver' 
                            ? 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)' 
                            : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
                          padding: '12px 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {/* Subtle pattern */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '150px',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1))',
                            transform: 'skewX(-20deg)',
                            transformOrigin: 'top right'
                          }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', zIndex: 1 }}>
                            <span style={{ fontSize: '1.1rem' }}>{item.source === 'waiver' ? '📋' : '📝'}</span>
                            <span style={{ color: 'white', fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {item.source === 'waiver' ? 'IRS Waiver' : 'Consent Form'}
                            </span>
                          </div>
                          <div style={{ 
                            background: 'rgba(255,255,255,0.2)', 
                            backdropFilter: 'blur(8px)',
                            padding: '4px 12px', 
                            borderRadius: '20px',
                            border: '1px solid rgba(255,255,255,0.3)',
                            position: 'relative',
                            zIndex: 1
                          }}>
                            <span style={{ color: 'white', fontSize: '0.8rem', fontWeight: '600' }}>✓ Completed</span>
                          </div>
                        </div>
                        
                        <div style={{ 
                          padding: isMobile ? '14px' : '18px 22px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '14px',
                          flexWrap: 'wrap'
                        }}>
                          {/* Left: Checkbox + Info */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1', minWidth: '180px' }}>
                            <input
                              type="checkbox"
                              checked={selectedCompletedForms.includes(item.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedCompletedForms(prev => [...prev, item.id]);
                                else setSelectedCompletedForms(prev => prev.filter(id => id !== item.id));
                              }}
                              style={{ 
                                width: '22px', 
                                height: '22px', 
                                cursor: 'pointer', 
                                accentColor: item.source === 'waiver' ? '#10b981' : '#3b82f6', 
                                flexShrink: 0 
                              }}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.05rem', marginBottom: '6px' }}>
                                {item.taxpayerInfo?.name || item.taxpayerName || 'Unknown'}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                                <span style={{ 
                                  fontSize: '0.9rem', 
                                  color: '#475569',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  📅 TY {item.taxYear}
                                </span>
                                <span style={{ 
                                  fontSize: '0.85rem', 
                                  color: '#94a3b8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  🕐 {(() => {
                                    const d = new Date(item.signedAt || item.submittedAt || item.createdAt);
                                    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Right: Action Buttons */}
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button
                              onClick={() => downloadIRSWaiverPDF(item, company, preparer)}
                              style={{ 
                                padding: isMobile ? '10px 14px' : '10px 16px', 
                                background: 'linear-gradient(135deg, #10b981, #059669)', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                fontWeight: '600', 
                                fontSize: '0.85rem',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              📥 {isMobile ? '' : 'PDF'}
                            </button>
                            <button
                              onClick={() => item.source === 'waiver' ? setViewingWaiverSubmission(item) : setViewingConsent(item)}
                              style={{ 
                                padding: isMobile ? '10px 14px' : '10px 16px', 
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                fontWeight: '600', 
                                fontSize: '0.85rem',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              👁️ {isMobile ? '' : 'View'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Old Pending Requests Section - Kept for backward compatibility */}
            {consentRequests.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ 
                color: '#f59e0b', 
                marginBottom: '15px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '1.1rem'
              }}>
                <span style={{ 
                  background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', 
                  padding: '8px 14px', 
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
                }}>
                  ⏳ Pending Email Requests
                  <span style={{ 
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)', 
                    color: 'white', 
                    padding: '3px 10px', 
                    borderRadius: '12px', 
                    fontSize: '0.85rem',
                    fontWeight: '700'
                  }}>
                    {consentRequests.length}
                  </span>
                </span>
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {consentRequests.map(request => (
                  <div key={request.id} style={{ 
                    background: 'white', 
                    border: '1px solid #fcd34d', 
                    borderRadius: '12px', 
                    padding: '0',
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                    overflow: 'hidden'
                  }}>
                    <div style={{ height: '4px', background: 'linear-gradient(90deg, #f59e0b, #d97706)' }} />
                    <div style={{ 
                      padding: isMobile ? '14px' : '16px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div>
                        <h4 style={{ margin: '0 0 6px 0', color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>{request.taxpayerName}</h4>
                        <p style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.9rem' }}>📧 {request.taxpayerEmail}</p>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
                          Sent: {formatConsentDate(request.createdAt)} • Tax Year: {request.taxYear}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => copyConsentLink(request.id)}
                          style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}
                        >
                          🔗 Copy Link
                        </button>
                        <button
                          onClick={() => handleDeleteConsentRequest(request.id)}
                          style={{ padding: '10px 16px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', minHeight: '44px' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        )}

        {/* Tax Returns Tab */}
        {activeTab === 'taxreturns' && (
          <div style={{ padding: '0' }}>
            {/* Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
              borderRadius: '16px 16px 0 0',
              padding: 'clamp(16px, 4vw, 25px) clamp(16px, 4vw, 30px)',
              color: 'white',
              marginBottom: '0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0
                }}>
                  💰
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2 style={{ margin: 0, fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: '700' }}>Tax Returns Tracker</h2>
                  <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>Track client tax returns and payment status</p>
                </div>
              </div>
            </div>

            {/* Grand Total Summary */}
            <div style={{ 
              background: 'white',
              padding: '20px 25px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
              justifyContent: 'space-around',
              alignItems: 'center'
            }}>
              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>💵 Grand Total</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                  ${taxReturnsGrandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>✅ Paid</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                  ${taxReturnsPaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>⏳ Unpaid</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444' }}>
                  ${taxReturnsUnpaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>📊 Total Records</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
                  {taxReturns.length}
                </div>
              </div>
              {/* Export Button */}
              {taxReturns.length > 0 && (
                <button
                  onClick={() => {
                    // Generate CSV content
                    const headers = ['Client Name', 'Phone', 'Tax Year', 'Amount', 'Payment Status'];
                    const rows = taxReturns.map(tr => [
                      tr.clientName,
                      tr.phone || '',
                      tr.taxYear,
                      tr.amount,
                      tr.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'
                    ]);
                    
                    // Add summary row
                    rows.push([]);
                    rows.push(['SUMMARY']);
                    rows.push(['Total Records:', taxReturns.length]);
                    rows.push(['Grand Total:', '$' + taxReturnsGrandTotal.toFixed(2)]);
                    rows.push(['Total Paid:', '$' + taxReturnsPaidTotal.toFixed(2)]);
                    rows.push(['Total Unpaid:', '$' + taxReturnsUnpaidTotal.toFixed(2)]);
                    
                    const csvContent = [headers, ...rows]
                      .map(row => row.map(cell => `"${cell}"`).join(','))
                      .join('\n');
                    
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `tax_returns_${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  📥 Export CSV
                </button>
              )}
            </div>

            {/* Input Form */}
            <div style={{ 
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  color: '#374151', 
                  fontSize: '0.95rem', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    background: editingTaxReturn ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #10b981, #059669)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem'
                  }}>
                    {editingTaxReturn ? '✏️' : '➕'}
                  </span>
                  {editingTaxReturn ? 'Edit Tax Return' : 'Add New Tax Return'}
                </h3>
              </div>
              <form onSubmit={handleSubmitTaxReturn}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  {/* Client Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4b5563', fontSize: '0.75rem' }}>
                      Client Name *
                    </label>
                    <input
                      type="text"
                      name="clientName"
                      value={taxReturnForm.clientName}
                      onChange={handleTaxReturnFormChange}
                      placeholder="Enter client name"
                      required
                      style={{ 
                        width: '100%', 
                        padding: '8px 10px', 
                        borderRadius: '6px', 
                        border: '1px solid #d1d5db', 
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4b5563', fontSize: '0.75rem' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={taxReturnForm.phone}
                      onChange={handleTaxReturnFormChange}
                      placeholder="(555) 123-4567"
                      style={{ 
                        width: '100%', 
                        padding: '8px 10px', 
                        borderRadius: '6px', 
                        border: '1px solid #d1d5db', 
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4b5563', fontSize: '0.75rem' }}>
                      Amount *
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={taxReturnForm.amount}
                      onChange={handleTaxReturnFormChange}
                      placeholder="0.00"
                      required
                      min="0"
                      step="0.01"
                      style={{ 
                        width: '100%', 
                        padding: '8px 10px', 
                        borderRadius: '6px', 
                        border: '1px solid #d1d5db', 
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    />
                  </div>

                  {/* Tax Year */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4b5563', fontSize: '0.75rem' }}>
                      Tax Year
                    </label>
                    <select
                      name="taxYear"
                      value={taxReturnForm.taxYear}
                      onChange={handleTaxReturnFormChange}
                      style={{ 
                        width: '100%', 
                        padding: '8px 10px', 
                        borderRadius: '6px', 
                        border: '1px solid #d1d5db', 
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    >
                      {[...Array(10)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <option key={year} value={year}>{year}</option>;
                      })}
                    </select>
                  </div>

                  {/* Payment Status */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#4b5563', fontSize: '0.75rem' }}>
                      Payment Status
                    </label>
                    <select
                      name="paymentStatus"
                      value={taxReturnForm.paymentStatus}
                      onChange={handleTaxReturnFormChange}
                      style={{ 
                        width: '100%', 
                        padding: '8px 10px', 
                        borderRadius: '6px', 
                        border: '1px solid #d1d5db', 
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>

                  {/* Action Buttons - Inline */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <button
                      type="submit"
                      disabled={savingTaxReturn}
                      style={{ 
                        padding: '8px 16px', 
                        background: editingTaxReturn 
                          ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
                          : 'linear-gradient(135deg, #10b981, #059669)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '0.8rem', 
                        fontWeight: '600', 
                        cursor: savingTaxReturn ? 'not-allowed' : 'pointer',
                        opacity: savingTaxReturn ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {savingTaxReturn ? '⏳' : (editingTaxReturn ? '💾' : '➕')}
                      {savingTaxReturn ? 'Saving...' : (editingTaxReturn ? 'Update' : 'Add')}
                    </button>
                    {editingTaxReturn && (
                      <button
                        type="button"
                        onClick={handleCancelEditTaxReturn}
                        style={{ 
                          padding: '8px 12px', 
                          background: '#f1f5f9', 
                          color: '#64748b', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '6px', 
                          fontSize: '0.8rem', 
                          fontWeight: '500', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        ✕ Cancel
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Tax Returns Table */}
            <div style={{ 
              background: 'white',
              borderRadius: '0 0 16px 16px',
              overflow: 'hidden'
            }}>
              {taxReturns.length === 0 ? (
                <div style={{ 
                  padding: '60px 20px', 
                  textAlign: 'center',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>📋</div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#374151' }}>No Tax Returns Yet</h3>
                  <p style={{ margin: 0 }}>Add your first tax return entry using the form above.</p>
                </div>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="mobile-tax-returns" style={{ 
                    display: 'none',
                    padding: '12px'
                  }}>
                    <style>{`
                      @media (max-width: 768px) {
                        .mobile-tax-returns { display: block !important; }
                        .desktop-tax-returns { display: none !important; }
                      }
                    `}</style>
                    {taxReturns.map((tr, index) => (
                      <div 
                        key={tr.id} 
                        style={{ 
                          background: '#f8fafc',
                          borderRadius: '12px',
                          padding: '14px',
                          marginBottom: '10px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        {/* Header with name and status */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '10px'
                        }}>
                          <div>
                            <h4 style={{ 
                              margin: '0 0 4px 0', 
                              fontSize: '0.95rem', 
                              fontWeight: '600', 
                              color: '#1e293b' 
                            }}>
                              {tr.clientName}
                            </h4>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '0.8rem', 
                              color: '#64748b' 
                            }}>
                              {tr.phone || 'No phone'}
                            </p>
                          </div>
                          <span style={{ 
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            background: tr.paymentStatus === 'paid' ? '#dcfce7' : '#fee2e2',
                            color: tr.paymentStatus === 'paid' ? '#166534' : '#dc2626'
                          }}>
                            {tr.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
                          </span>
                        </div>
                        
                        {/* Details row */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '10px',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', gap: '16px' }}>
                            <div>
                              <span style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>Year</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#374151' }}>{tr.taxYear}</span>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>Amount</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#10b981' }}>
                                ${tr.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleEditTaxReturn(tr)}
                              style={{ 
                                padding: isMobile ? '12px 14px' : '8px 12px', 
                                background: '#3b82f6', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                cursor: 'pointer',
                                fontSize: isMobile ? '1rem' : '0.85rem',
                                fontWeight: '600',
                                minHeight: isMobile ? '44px' : '36px',
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'manipulation'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteTaxReturn(tr.id)}
                              style={{ 
                                padding: isMobile ? '12px 14px' : '8px 12px', 
                                background: '#ef4444', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                cursor: 'pointer',
                                fontSize: isMobile ? '1rem' : '0.85rem',
                                fontWeight: '600',
                                minHeight: isMobile ? '44px' : '36px',
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'manipulation'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="desktop-tax-returns" style={{ overflowX: 'auto' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      minWidth: '600px'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Client Name</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Phone</th>
                          <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Tax Year</th>
                          <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Amount</th>
                          <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Status</th>
                          <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxReturns.map((tr, index) => (
                          <tr 
                            key={tr.id} 
                            style={{ 
                              borderBottom: '1px solid #e5e7eb',
                              background: index % 2 === 0 ? 'white' : '#fafafa'
                            }}
                          >
                            <td style={{ padding: '14px 16px', fontWeight: '500', color: '#1e293b' }}>
                              {tr.clientName}
                            </td>
                            <td style={{ padding: '14px 16px', color: '#64748b' }}>
                              {tr.phone || '-'}
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b' }}>
                              {tr.taxYear}
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                              ${tr.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                              <span style={{ 
                                display: 'inline-block',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                background: tr.paymentStatus === 'paid' ? '#dcfce7' : '#fee2e2',
                                color: tr.paymentStatus === 'paid' ? '#166534' : '#dc2626'
                              }}>
                                {tr.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
                              </span>
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleEditTaxReturn(tr)}
                                  style={{ 
                                    padding: '8px 14px', 
                                    background: '#3b82f6', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    minHeight: '38px',
                                    WebkitTapHighlightColor: 'transparent'
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteTaxReturn(tr.id)}
                                  style={{ 
                                    padding: '8px 14px', 
                                    background: '#ef4444', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    minHeight: '38px',
                                    WebkitTapHighlightColor: 'transparent'
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* New Booking Tab */}
        {activeTab === 'new-booking' && (
          <div style={{ padding: '0' }}>
            <div style={{ 
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div>
                <h2 style={{ 
                  fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', 
                  fontWeight: '700', 
                  color: '#1e293b', 
                  margin: '0 0 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>{editingAppointment ? '✏️ Edit Appointment' : '➕ Create New Booking'}</h2>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                  {editingAppointment 
                    ? `Editing appointment for ${editingAppointment.fullName || editingAppointment.clientName}`
                    : 'Manually add a new appointment for a client'}
                </p>
              </div>
              {editingAppointment && (
                <button
                  onClick={handleCancelEdit}
                  style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Cancel Edit
                </button>
              )}
            </div>

            {bookingSuccess && (
              <div style={{
                padding: '16px 24px',
                background: '#dcfce7',
                border: '1px solid #86efac',
                borderRadius: '12px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>✅</span>
                <div>
                  <strong style={{ color: '#166534' }}>{editingAppointment ? 'Appointment Updated!' : 'Booking Created Successfully!'}</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#22c55e', fontSize: '0.9rem' }}>
                    Redirecting to appointments...
                  </p>
                </div>
              </div>
            )}

            <div style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              <div style={{
                background: editingAppointment ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                padding: '20px 24px',
                color: 'white'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {editingAppointment ? '✏️ Update Appointment Details' : '📝 Appointment Details'}
                </h3>
              </div>

              <form onSubmit={handleSubmitNewBooking} style={{ padding: '28px', display: 'flex', flexDirection: 'column' }}>
                
                {/* Section 1: Personal Information */}
                <div style={{ marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '1rem',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>1</div>
                    <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1e293b' }}>Personal Information</h4>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginLeft: '48px' }}>
                    {/* Client Name - Full Width */}
                    <div style={{ maxWidth: '500px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Full Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={newBookingForm.clientName}
                        onChange={(e) => setNewBookingForm(prev => ({ ...prev, clientName: e.target.value }))}
                        placeholder="Enter client's full name"
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Email & Phone Row */}
                    <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: isSmallMobile ? '1fr' : '1fr 1fr', maxWidth: '500px' }}>
                      {/* Client Email */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={newBookingForm.clientEmail}
                          onChange={(e) => setNewBookingForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                          placeholder="client@email.com"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '10px',
                            fontSize: '1rem',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>

                      {/* Client Phone */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newBookingForm.clientPhone}
                          onChange={(e) => setNewBookingForm(prev => ({ ...prev, clientPhone: e.target.value }))}
                          placeholder="(555) 123-4567"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '10px',
                            fontSize: '1rem',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Appointment Details */}
                <div style={{ marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '1rem',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>2</div>
                    <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1e293b' }}>Appointment Details</h4>
                  </div>
                  
                  <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: isSmallMobile ? '1fr' : '120px 1fr', marginLeft: '48px', maxWidth: '500px', alignItems: 'end' }}>
                    {/* Number of Clients */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        # of Clients
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={newBookingForm.numberOfClients}
                        onChange={(e) => setNewBookingForm(prev => ({ ...prev, numberOfClients: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s',
                          outline: 'none',
                          textAlign: 'center'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Service Type */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Service Type <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={newBookingForm.fillingType}
                        onChange={(e) => setNewBookingForm(prev => ({ ...prev, fillingType: e.target.value }))}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          background: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        <option value="">Select a service</option>
                        <option value="Individual Tax Return">Individual Tax Return</option>
                        <option value="Business Tax Return">Business Tax Return</option>
                        <option value="Tax Planning">Tax Planning</option>
                        <option value="Bookkeeping">Bookkeeping</option>
                        <option value="Payroll Services">Payroll Services</option>
                        <option value="IRS Representation">IRS Representation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 3: Select Date & Time */}
                <div style={{ marginBottom: '24px', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '1rem',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>3</div>
                    <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1e293b' }}>Select Date & Time</h4>
                  </div>
                  
                  <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: isSmallMobile ? '1fr' : '1fr 1fr', marginLeft: '48px', maxWidth: '500px' }}>
                    {/* Date */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Date <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={newBookingForm.date}
                        onChange={(e) => setNewBookingForm(prev => ({ ...prev, date: e.target.value }))}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Time */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Time <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={newBookingForm.time}
                        onChange={(e) => setNewBookingForm(prev => ({ ...prev, time: e.target.value }))}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          background: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        <option value="">Select a time</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>
                            {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div style={{ marginTop: '16px', paddingTop: '24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '12px', justifyContent: 'flex-start', marginLeft: '48px' }}>
                  <button
                    type="submit"
                    disabled={savingBooking}
                    style={{
                      padding: '14px 32px',
                      background: savingBooking ? '#94a3b8' : editingAppointment ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: savingBooking ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: editingAppointment ? '0 2px 8px rgba(245, 158, 11, 0.3)' : '0 2px 8px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {savingBooking ? (
                      <>⏳ {editingAppointment ? 'Updating...' : 'Creating...'}</>
                    ) : (
                      <>{editingAppointment ? '✏️ Update Appointment' : '✅ Create Booking'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={editingAppointment ? handleCancelEdit : () => setNewBookingForm({
                      clientName: '',
                      clientEmail: '',
                      clientPhone: '',
                      numberOfClients: '1',
                      fillingType: '',
                      date: '',
                      time: '',
                      notes: ''
                    })}
                    style={{
                      padding: '14px 24px',
                      background: 'white',
                      color: '#64748b',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {editingAppointment ? '✕ Cancel' : 'Clear Form'}
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Info */}
            <div style={{
              marginTop: '24px',
              padding: '16px 20px',
              background: editingAppointment ? '#fef3c7' : '#f0f9ff',
              border: editingAppointment ? '1px solid #fcd34d' : '1px solid #bae6fd',
              borderRadius: '12px'
            }}>
              <p style={{ margin: 0, color: editingAppointment ? '#92400e' : '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span>💡</span>
                <span>
                  {editingAppointment ? (
                    <>
                      <strong>Tip:</strong> Update the appointment details. A confirmation email will only be sent if you change the email address.
                    </>
                  ) : (
                    <>
                      <strong>Tip:</strong> Appointments created here will be automatically marked as "Confirmed". 
                      The client will be notified by email if an email address is provided.
                    </>
                  )}
                </span>
              </p>
            </div>
          </div>
        )}

      {/* Send Document Modal */}
      {showSendDocumentModal && selectedTemplate && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px' }}>
          <div className="modal-content" style={{ maxWidth: '450px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.2rem' }}>📤 Send Document</h2>
              <button 
                onClick={() => {
                  setShowSendDocumentModal(false);
                  setSelectedTemplate(null);
                  setDocumentSendForm({ clientName: '', clientEmail: '' });
                }}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '25px' }}>
              <div style={{ background: '#faf5ff', padding: '15px', borderRadius: '10px', marginBottom: '20px' }}>
                <p style={{ margin: 0, color: '#6d28d9', fontSize: '0.9rem' }}>
                  <strong>Template:</strong> {selectedTemplate.name}
                </p>
                <p style={{ margin: '5px 0 0 0', color: '#9333ea', fontSize: '0.85rem' }}>
                  {selectedTemplate.fields?.length || 0} fields to fill
                </p>
              </div>
              
              <form onSubmit={handleSendDocument}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    Client Name *
                  </label>
                  <input
                    type="text"
                    value={documentSendForm.clientName}
                    onChange={(e) => setDocumentSendForm(prev => ({ ...prev, clientName: e.target.value }))}
                    placeholder="Enter client's full name"
                    required
                    style={{ width: '100%', padding: '12px 15px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    Client Email *
                  </label>
                  <input
                    type="email"
                    value={documentSendForm.clientEmail}
                    onChange={(e) => setDocumentSendForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                    placeholder="client@email.com"
                    required
                    style={{ width: '100%', padding: '12px 15px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSendDocumentModal(false);
                      setSelectedTemplate(null);
                      setDocumentSendForm({ clientName: '', clientEmail: '' });
                    }}
                    style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingDocument}
                    style={{ flex: 1, padding: '12px', background: sendingDocument ? '#c4b5fd' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: 'white', border: 'none', borderRadius: '10px', cursor: sendingDocument ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem' }}
                  >
                    {sendingDocument ? '⏳ Sending...' : '📤 Send Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preparer Fill & Sign Modal for Pending Requests */}
      {editingPendingRequest && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px' }}>
          <div className="modal-content" style={{ maxWidth: '550px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.15rem' }}>📝 Review & Complete</h2>
                <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                  {editingPendingRequest._parentTemplate?.name || 'Document'}
                </p>
              </div>
              <button 
                onClick={() => { setEditingPendingRequest(null); setPendingFormValues({}); }}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem' }}
              >×</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {/* Client Info */}
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0369a1', marginBottom: '4px' }}>👤 Client Info</div>
                <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: '600' }}>{editingPendingRequest.clientName || 'Not provided'}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{editingPendingRequest.clientEmail || 'No email'}</div>
              </div>

              {/* All fields grouped */}
              {(() => {
                const fields = editingPendingRequest._templateFields || [];
                const preparerFields = fields.filter(f => f.filledBy === 'preparer');
                const clientFields = fields.filter(f => f.filledBy !== 'preparer');

                return (
                  <>
                    {/* Preparer Fields - Editable */}
                    {preparerFields.length > 0 && (
                      <div style={{ marginBottom: '18px' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#6d28d9', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          📋 Preparer Fields <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#8b5cf6' }}>(fill these)</span>
                        </div>
                        {preparerFields.map(field => (
                          <div key={field.id} style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                              {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                            </label>
                            {field.type === 'checkbox' ? (
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={pendingFormValues[field.id] || false}
                                  onChange={e => setPendingFormValues(prev => ({ ...prev, [field.id]: e.target.checked }))}
                                  style={{ width: '18px', height: '18px', accentColor: '#8b5cf6' }}
                                />
                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>Yes</span>
                              </label>
                            ) : field.type === 'signature' || field.type === 'initials' ? (
                              <input
                                type="text"
                                value={pendingFormValues[field.id] || ''}
                                onChange={e => setPendingFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                placeholder={field.type === 'signature' ? 'Type your full name as signature' : 'Type your initials'}
                                style={{ width: '100%', padding: '10px 14px', border: '2px solid #c4b5fd', borderRadius: '10px', fontSize: '0.95rem', boxSizing: 'border-box', fontFamily: "'Brush Script MT', 'Dancing Script', cursive", fontStyle: 'italic' }}
                              />
                            ) : field.type === 'date' ? (
                              <input
                                type="date"
                                value={pendingFormValues[field.id] || ''}
                                onChange={e => setPendingFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                style={{ width: '100%', padding: '10px 14px', border: '2px solid #c4b5fd', borderRadius: '10px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                              />
                            ) : (
                              <input
                                type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                                value={pendingFormValues[field.id] || ''}
                                onChange={e => setPendingFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                style={{ width: '100%', padding: '10px 14px', border: '2px solid #c4b5fd', borderRadius: '10px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Client Fields - Read-only info */}
                    {clientFields.length > 0 && (
                      <div style={{ marginBottom: '18px' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0369a1', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          👤 Client Fields <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#64748b' }}>(client will fill)</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {clientFields.map(f => (
                            <span key={f.id} style={{ fontSize: '0.78rem', background: '#f1f5f9', color: '#64748b', padding: '4px 10px', borderRadius: '8px', fontWeight: '500', border: '1px solid #e2e8f0' }}>
                              {f.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Actions */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px', flexShrink: 0 }}>
              <button
                onClick={() => { setEditingPendingRequest(null); setPendingFormValues({}); }}
                style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem' }}
              >
                Cancel
              </button>
              <button
                disabled={savingPendingRequest}
                onClick={async () => {
                  const fields = editingPendingRequest._templateFields || [];
                  const preparerFields = fields.filter(f => f.filledBy === 'preparer');
                  
                  // Validate required preparer fields
                  for (const field of preparerFields) {
                    if (field.required) {
                      const val = pendingFormValues[field.id];
                      if (field.type === 'checkbox' ? !val : !val?.toString().trim()) {
                        alert(`Please fill in "${field.label}"`);
                        return;
                      }
                    }
                  }

                  setSavingPendingRequest(true);
                  try {
                    await submitSignedDocument(editingPendingRequest.id, {
                      fieldValues: pendingFormValues,
                      signatures: {},
                      typedSignatures: {},
                      clientInfo: {
                        name: editingPendingRequest.clientName || preparer?.name || '',
                        email: editingPendingRequest.clientEmail || ''
                      }
                    });
                    alert('✅ Document signed and completed successfully!');
                    setEditingPendingRequest(null);
                    setPendingFormValues({});
                  } catch (err) {
                    console.error('Error signing document:', err);
                    alert('❌ Failed to complete document. Please try again.');
                  } finally {
                    setSavingPendingRequest(false);
                  }
                }}
                style={{ flex: 1, padding: '12px', background: savingPendingRequest ? '#86efac' : 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '10px', cursor: savingPendingRequest ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.95rem' }}
              >
                {savingPendingRequest ? '⏳ Saving...' : '✅ Sign & Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completed Document PDF Viewer Modal */}
      {viewingCompletedDoc && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001, padding: '10px' }}>
          <div style={{ width: '95vw', maxWidth: '900px', height: '95vh', background: 'white', borderRadius: '16px', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>📄 {viewingCompletedDoc.templateName}</h2>
                <p style={{ margin: '3px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                  {viewingCompletedDoc.clientName} · Signed {viewingCompletedDoc.signedAt ? new Date(viewingCompletedDoc.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </p>
              </div>
              <button
                onClick={() => setViewingCompletedDoc(null)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.3rem' }}
              >×</button>
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0, flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => setViewerScale(s => Math.max(0.5, s - 0.1))} style={{ padding: '6px 12px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>−</button>
                <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '600', minWidth: '45px', textAlign: 'center' }}>{Math.round(viewerScale * 100)}%</span>
                <button onClick={() => setViewerScale(s => Math.min(2, s + 0.1))} style={{ padding: '6px 12px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>+</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => setViewerCurrentPage(p => Math.max(1, p - 1))} disabled={viewerCurrentPage <= 1} style={{ padding: '6px 12px', background: viewerCurrentPage <= 1 ? '#f1f5f9' : '#e2e8f0', border: 'none', borderRadius: '6px', cursor: viewerCurrentPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: '600', color: viewerCurrentPage <= 1 ? '#94a3b8' : '#1e293b' }}>← Prev</button>
                <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '600' }}>Page {viewerCurrentPage} of {viewerNumPages || '?'}</span>
                <button onClick={() => setViewerCurrentPage(p => Math.min(viewerNumPages || p, p + 1))} disabled={viewerCurrentPage >= viewerNumPages} style={{ padding: '6px 12px', background: viewerCurrentPage >= viewerNumPages ? '#f1f5f9' : '#e2e8f0', border: 'none', borderRadius: '6px', cursor: viewerCurrentPage >= viewerNumPages ? 'not-allowed' : 'pointer', fontWeight: '600', color: viewerCurrentPage >= viewerNumPages ? '#94a3b8' : '#1e293b' }}>Next →</button>
              </div>
              {/* Client Info Badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.78rem', background: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '8px', fontWeight: '600' }}>
                  ✅ {viewingCompletedDoc.clientName}
                </span>
                {viewingCompletedDoc.clientEmail && (
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{viewingCompletedDoc.clientEmail}</span>
                )}
              </div>
            </div>

            {/* PDF Content with Field Overlays */}
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '20px', background: '#e2e8f0' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <PdfDocument
                  file={viewingCompletedDoc.pdfData}
                  onLoadSuccess={({ numPages }) => setViewerNumPages(numPages)}
                  loading={<div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading PDF...</div>}
                  error={<div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Failed to load PDF</div>}
                >
                  <PdfPage
                    pageNumber={viewerCurrentPage}
                    scale={viewerScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </PdfDocument>

                {/* Field Values Overlay */}
                {(viewingCompletedDoc.fields || [])
                  .filter(f => f.page === viewerCurrentPage)
                  .map(field => {
                    const val = viewingCompletedDoc.fieldValues?.[field.id];
                    const sig = viewingCompletedDoc.signatures?.[field.id];
                    const isSignature = field.type === 'signature' || field.type === 'initials';
                    const isCheckbox = field.type === 'checkbox';

                    return (
                      <div
                        key={field.id}
                        style={{
                          position: 'absolute',
                          left: field.x * viewerScale,
                          top: field.y * viewerScale,
                          width: field.width * viewerScale,
                          height: field.height * viewerScale,
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isCheckbox ? 'center' : 'flex-start',
                          padding: isCheckbox ? 0 : '0 4px',
                          overflow: 'hidden'
                        }}
                      >
                        {sig ? (
                          <img src={sig} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : isSignature && val ? (
                          <span style={{
                            fontFamily: '"Brush Script MT", "Dancing Script", cursive',
                            fontSize: Math.min(20, field.height * viewerScale * 0.65) + 'px',
                            color: '#1e3a8a',
                            fontWeight: '600'
                          }}>{val}</span>
                        ) : isCheckbox ? (
                          <div style={{
                            width: Math.max(14, Math.min(field.width, field.height) * viewerScale * 0.8),
                            height: Math.max(14, Math.min(field.width, field.height) * viewerScale * 0.8),
                            backgroundColor: val ? '#22c55e' : 'white',
                            border: `1px solid ${val ? '#22c55e' : '#cbd5e1'}`,
                            borderRadius: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {val && <span style={{ color: 'white', fontWeight: 'bold', fontSize: Math.max(10, Math.min(field.width, field.height) * viewerScale * 0.5) }}>✓</span>}
                          </div>
                        ) : val ? (
                          <span style={{
                            fontSize: Math.min(14, field.height * viewerScale * 0.6) + 'px',
                            color: '#1e293b',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>{val}</span>
                        ) : (
                          <span style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>—</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Cropper Modal */}
      {showCropper && cropImage && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '500px', background: 'white', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>📷 Adjust Profile Photo</h2>
              <button onClick={() => { setShowCropper(false); setCropImage(null); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ position: 'relative', height: '350px', background: '#1a1a2e' }}>
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />
            </div>
            <div style={{ padding: '15px 20px', background: '#f8fafc' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>🔍 Zoom</label>
              <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ width: '100%', marginBottom: '15px' }} />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowCropper(false); setCropImage(null); }} style={{ padding: '10px 20px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                <button onClick={handleCropConfirm} style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>✓ Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: isMobile ? '0' : '15px', overflowY: 'auto' }}>
          <div className="modal-content" style={{ maxWidth: isMobile ? '100%' : '500px', width: '100%', height: isMobile ? '100%' : 'auto', background: 'white', borderRadius: isMobile ? '0' : '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', margin: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>✏️ Edit My Profile</h2>
              <button onClick={() => setShowEditProfile(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '18px', flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: isMobile ? '100px' : '18px' }}>
              <form onSubmit={handleSaveProfile}>
                
                {/* Photo Upload Section */}
                <div style={{ marginBottom: '16px', padding: '14px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#0369a1', fontSize: '0.9rem' }}>
                    📷 Profile Photo
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <div style={{ 
                      width: '110px', 
                      height: '110px', 
                      borderRadius: '50%', 
                      border: editForm.photo ? '3px solid #3b82f6' : '2px dashed #94a3b8',
                      background: editForm.photo ? '#fff' : '#f1f5f9',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {editForm.photo ? (
                        <img src={editForm.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '2.5rem', color: '#94a3b8' }}>👤</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} id="edit-profile-photo" style={{ display: 'none' }} />
                      <label htmlFor="edit-profile-photo" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#3b82f6', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}>
                        📤 {editForm.photo ? 'Change' : 'Upload'}
                      </label>
                      {editForm.photo && (
                        <button type="button" onClick={() => setEditForm(prev => ({ ...prev, photo: '' }))} style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.8rem' }}>
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name Field */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                    Full Name
                  </label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Your full name" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }} />
                </div>

                {/* Email & Phone */}
                <div style={{ display: 'grid', gridTemplateColumns: isExtraSmallMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📧 Email</label>
                    <input type="email" value={editForm.email} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📞 Phone</label>
                    <input type="tel" value={editForm.phone} onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* Address */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📍 Address</label>
                  <input type="text" value={editForm.address} onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Address (optional)" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }} />
                </div>

                {/* Bio */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📝 Bio</label>
                  <textarea value={editForm.bio} onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))} placeholder="Tell clients about yourself, your experience, specialties..." rows="4" maxLength={500} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>{editForm.bio?.length || 0}/500</div>
                </div>

                {/* Push Notification Toggle */}
                {isPushSupported() && (
                  <div style={{ marginBottom: '16px', padding: '14px', background: notificationsEnabled ? '#ecfdf5' : isVapidKeyConfigured() ? '#f8fafc' : '#fefce8', borderRadius: '12px', border: `1px solid ${notificationsEnabled ? '#a7f3d0' : isVapidKeyConfigured() ? '#e2e8f0' : '#fcd34d'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', color: notificationsEnabled ? '#047857' : isVapidKeyConfigured() ? '#374151' : '#92400e', fontSize: '0.9rem' }}>
                          {notificationsEnabled ? '🔔' : '🔕'} Push Notifications
                          {!isVapidKeyConfigured() && <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#d97706' }}>⚠️ Setup Required</span>}
                        </label>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                          {notificationPermission === 'denied' 
                            ? 'Blocked in browser settings' 
                            : !isVapidKeyConfigured()
                              ? 'Contact admin to configure push notifications'
                              : notificationsEnabled 
                                ? 'Get alerts when clients book' 
                                : 'Enable to receive booking alerts'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleToggleNotifications}
                        disabled={notificationsLoading || notificationPermission === 'denied'}
                        style={{
                          padding: '10px 18px',
                          background: notificationsLoading 
                            ? '#94a3b8' 
                            : notificationPermission === 'denied'
                              ? '#fecaca'
                              : notificationsEnabled 
                                ? '#fecaca' 
                                : !isVapidKeyConfigured()
                                  ? '#fbbf24'
                                  : '#10b981',
                          color: notificationPermission === 'denied' ? '#dc2626' : 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          cursor: notificationsLoading || notificationPermission === 'denied' ? 'not-allowed' : 'pointer',
                          minWidth: '90px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {notificationsLoading 
                          ? '...' 
                          : notificationPermission === 'denied'
                            ? 'Blocked'
                            : notificationsEnabled 
                              ? 'Disable' 
                              : 'Enable'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  justifyContent: 'flex-end', 
                  paddingTop: '14px', 
                  borderTop: '1px solid #e5e7eb',
                  ...(isMobile ? {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '16px 20px',
                    background: 'white',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                    zIndex: 100,
                    borderTop: '1px solid #e5e7eb'
                  } : {})
                }}>
                  <button type="button" onClick={() => setShowEditProfile(false)} style={{ padding: isMobile ? '14px 20px' : '8px 18px', flex: isMobile ? 1 : 'none', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontSize: isMobile ? '1rem' : '0.9rem', fontWeight: '500', cursor: 'pointer', minHeight: '44px' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={savingProfile} style={{ padding: isMobile ? '14px 20px' : '8px 18px', flex: isMobile ? 1 : 'none', background: savingProfile ? '#94a3b8' : '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontSize: isMobile ? '1rem' : '0.9rem', fontWeight: '600', cursor: savingProfile ? 'not-allowed' : 'pointer', minHeight: '44px' }}>
                    {savingProfile ? '⏳ Saving...' : '✓ Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
            </ContentCard>
          </motion.div>
        </AnimatePresence>
      </MainContent>

      {/* Send IRS Waiver Form Modal */}
      {showSendConsentModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: isMobile ? '0' : '15px', overflowY: 'auto' }}>
          <div className="modal-content" style={{ maxWidth: isMobile ? '100%' : '500px', width: '100%', height: isMobile ? '100%' : 'auto', background: 'white', borderRadius: isMobile ? '0' : '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', margin: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>✉️ Send IRS Waiver Form</h2>
              <button onClick={() => setShowSendConsentModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            
            <div style={{ padding: '20px', flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: isMobile ? '100px' : '20px' }}>
              <p style={{ marginTop: 0, marginBottom: '20px', color: '#64748b', fontSize: '0.9rem' }}>
                Send a secure link to your client to complete the IRS Waiver form with client info, photo upload, and electronic signature.
              </p>
              
              <form onSubmit={handleSendConsent}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Taxpayer Full Name *
                  </label>
                  <input
                    type="text"
                    name="taxpayerName"
                    value={consentForm.taxpayerName}
                    onChange={handleConsentFormChange}
                    placeholder="John Smith"
                    required
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Taxpayer Email *
                  </label>
                  <input
                    type="email"
                    name="taxpayerEmail"
                    value={consentForm.taxpayerEmail}
                    onChange={handleConsentFormChange}
                    placeholder="john@email.com"
                    required
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: isExtraSmallMobile ? '1fr' : '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      name="taxpayerPhone"
                      value={consentForm.taxpayerPhone}
                      onChange={handleConsentFormChange}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      Tax Year
                    </label>
                    <select
                      name="taxYear"
                      value={consentForm.taxYear}
                      onChange={handleConsentFormChange}
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                    >
                      {[...Array(5)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <option key={year} value={year}>{year}</option>;
                      })}
                    </select>
                  </div>
                </div>
                
                <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '10px', marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#0369a1', fontSize: '0.9rem' }}>📋 What the client will complete:</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li>IRS Notice 2014-7 Acknowledgment & Election</li>
                    <li>Client Information (Taxpayer, Spouse, Dependents)</li>
                    <li>Photo ID Upload for Verification</li>
                    <li>Engagement Letter Agreement</li>
                    <li>Electronic Signature (Draw, Type, or QR Scan)</li>
                  </ul>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  justifyContent: 'flex-end',
                  ...(isMobile ? {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '16px 20px',
                    background: 'white',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                    zIndex: 100,
                    borderTop: '1px solid #e5e7eb'
                  } : {})
                }}>
                  <button 
                    type="button" 
                    onClick={() => setShowSendConsentModal(false)}
                    style={{ padding: isMobile ? '14px 20px' : '10px 20px', flex: isMobile ? 1 : 'none', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontSize: isMobile ? '1rem' : '0.9rem', fontWeight: '500', cursor: 'pointer', minHeight: '44px' }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={sendingConsent}
                    style={{ padding: isMobile ? '14px 24px' : '10px 24px', flex: isMobile ? 1 : 'none', background: sendingConsent ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: isMobile ? '1rem' : '0.9rem', fontWeight: '600', cursor: sendingConsent ? 'not-allowed' : 'pointer', minHeight: '44px' }}
                  >
                    {sendingConsent ? '⏳ Sending...' : '✉️ Send Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Signed Consent Modal */}
      {viewingConsent && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px', overflowY: 'auto' }}>
          <div className="modal-content" style={{ maxWidth: '700px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', margin: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>📋 Signed Consent Document</h2>
              <button onClick={() => setViewingConsent(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {/* Taxpayer Information */}
              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1rem' }}>👤 Taxpayer Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Name</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Email</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.email}</span>
                  </div>
                  {viewingConsent.taxpayerInfo?.phone && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Phone</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo.phone}</span>
                    </div>
                  )}
                  {viewingConsent.taxpayerInfo?.ssn && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingConsent.taxpayerInfo.ssn}</span>
                    </div>
                  )}
                  {viewingConsent.taxpayerInfo?.address && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Address</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo.address}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Document Details */}
              <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '1rem' }}>📄 Document Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#166534', fontSize: '0.8rem', display: 'block' }}>Tax Year</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxYear}</span>
                  </div>
                  <div>
                    <span style={{ color: '#166534', fontSize: '0.8rem', display: 'block' }}>Signed Date</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{formatConsentDate(viewingConsent.signedAt)}</span>
                  </div>
                  <div>
                    <span style={{ color: '#166534', fontSize: '0.8rem', display: 'block' }}>Document ID</span>
                    <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.85rem' }}>{viewingConsent.id?.substring(0, 12)}...</span>
                  </div>
                </div>
              </div>
              
              {/* Consents Given */}
              <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '1rem' }}>✅ Consents Given</h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {viewingConsent.consents?.disclosure && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>IRC §7216 Disclosure Consent</span>
                    </div>
                  )}
                  {viewingConsent.consents?.useConsent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Consent to Use Tax Return Information</span>
                    </div>
                  )}
                  {viewingConsent.consents?.efileAuth && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>IRS E-File Signature Authorization</span>
                    </div>
                  )}
                  {viewingConsent.consents?.incomeWaiver && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#f59e0b' }}>📝</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Income Documentation Waiver</span>
                    </div>
                  )}
                  {viewingConsent.consents?.privacyPolicy && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Privacy Policy Acknowledgment</span>
                    </div>
                  )}
                  {viewingConsent.consents?.feeAgreement && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>💰</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Fee Agreement Accepted</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Signature */}
              <div style={{ background: '#fefce8', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '1rem' }}>✍️ Electronic Signature</h3>
                {viewingConsent.signature?.image && (
                  <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '10px' }}>
                    <img 
                      src={viewingConsent.signature.image} 
                      alt="Signature" 
                      style={{ maxWidth: '100%', maxHeight: '100px' }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ color: '#92400e' }}>Typed Name: </span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.signature?.typed}</span>
                  </div>
                  <div>
                    <span style={{ color: '#92400e' }}>IP Address: </span>
                    <span style={{ color: '#64748b' }}>{viewingConsent.signature?.ipAddress || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => downloadIRSWaiverPDF(viewingConsent, company, preparer)}
                  style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  📥 Download PDF
                </button>
                <button 
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Tax Consent Document - ${viewingConsent.taxpayerInfo?.name}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                            h1 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
                            h2 { color: #374151; margin-top: 25px; }
                            .section { margin-bottom: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; }
                            .consent-item { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
                            .signature-area { border: 1px solid #d1d5db; padding: 20px; margin-top: 20px; text-align: center; }
                            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #64748b; }
                          </style>
                        </head>
                        <body>
                          <h1>Tax Consent Document</h1>
                          <div class="section">
                            <h2>Taxpayer Information</h2>
                            <p><strong>Name:</strong> ${viewingConsent.taxpayerInfo?.name}</p>
                            <p><strong>Email:</strong> ${viewingConsent.taxpayerInfo?.email}</p>
                            ${viewingConsent.taxpayerInfo?.phone ? `<p><strong>Phone:</strong> ${viewingConsent.taxpayerInfo.phone}</p>` : ''}
                            ${viewingConsent.taxpayerInfo?.address ? `<p><strong>Address:</strong> ${viewingConsent.taxpayerInfo.address}</p>` : ''}
                            <p><strong>Tax Year:</strong> ${viewingConsent.taxYear}</p>
                          </div>
                          <div class="section">
                            <h2>Consents Given</h2>
                            ${viewingConsent.consents?.disclosure ? '<div class="consent-item">✓ IRC §7216 Disclosure Consent</div>' : ''}
                            ${viewingConsent.consents?.useConsent ? '<div class="consent-item">✓ Consent to Use Tax Return Information</div>' : ''}
                            ${viewingConsent.consents?.efileAuth ? '<div class="consent-item">✓ IRS E-File Signature Authorization</div>' : ''}
                            ${viewingConsent.consents?.incomeWaiver ? '<div class="consent-item">⚠️ Income Documentation Waiver</div>' : ''}
                            ${viewingConsent.consents?.privacyPolicy ? '<div class="consent-item">✓ Privacy Policy Acknowledgment</div>' : ''}
                            ${viewingConsent.consents?.feeAgreement ? '<div class="consent-item">✓ Fee Agreement Accepted</div>' : ''}
                          </div>
                          <div class="signature-area">
                            <h2>Electronic Signature</h2>
                            ${viewingConsent.signature?.image ? `<img src="${viewingConsent.signature.image}" style="max-width: 300px; margin: 15px 0;" />` : ''}
                            <p><strong>Typed Name:</strong> ${viewingConsent.signature?.typed}</p>
                            <p><strong>Signed Date:</strong> ${formatConsentDate(viewingConsent.signedAt)}</p>
                          </div>
                          <div class="footer">
                            <p>Document ID: ${viewingConsent.id}</p>
                            <p>This document was electronically signed and is legally binding.</p>
                          </div>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }}
                  style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  🖨️ Print
                </button>
                <button 
                  onClick={() => setViewingConsent(null)}
                  style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View IRS Waiver Submission Modal */}
      {viewingWaiverSubmission && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px', overflowY: 'auto' }}>
          <div className="modal-content" style={{ maxWidth: '700px', width: '100%', background: 'white', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden', margin: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>📋 IRS Waiver Submission</h2>
              <button onClick={() => setViewingWaiverSubmission(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {/* Taxpayer Information */}
              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1rem' }}>👤 Taxpayer Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Name</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo?.name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Email</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo?.email}</span>
                  </div>
                  {viewingWaiverSubmission.taxpayerInfo?.phone && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Phone</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo.phone}</span>
                    </div>
                  )}
                  {viewingWaiverSubmission.taxpayerInfo?.ssn && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingWaiverSubmission.taxpayerInfo.ssn}</span>
                    </div>
                  )}
                  {viewingWaiverSubmission.taxpayerInfo?.address && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Address</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxpayerInfo.address}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Submission Details */}
              <div style={{ background: '#ede9fe', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#5b21b6', fontSize: '1rem' }}>📄 Submission Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#5b21b6', fontSize: '0.8rem', display: 'block' }}>Tax Year</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.taxYear}</span>
                  </div>
                  <div>
                    <span style={{ color: '#5b21b6', fontSize: '0.8rem', display: 'block' }}>Status</span>
                    <span style={{ 
                      background: viewingWaiverSubmission.status === 'verified' || viewingWaiverSubmission.status === 'completed' ? '#dcfce7' : '#fef3c7', 
                      color: viewingWaiverSubmission.status === 'verified' || viewingWaiverSubmission.status === 'completed' ? '#166534' : '#92400e',
                      padding: '3px 10px', 
                      borderRadius: '6px', 
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {viewingWaiverSubmission.status === 'verified' || viewingWaiverSubmission.status === 'completed' ? '✅ Verified' : '⏳ Pending'}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#5b21b6', fontSize: '0.8rem', display: 'block' }}>Submitted</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{formatConsentDate(viewingWaiverSubmission.submittedAt || viewingWaiverSubmission.createdAt)}</span>
                  </div>
                  <div>
                    <span style={{ color: '#5b21b6', fontSize: '0.8rem', display: 'block' }}>Document ID</span>
                    <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.85rem' }}>{viewingWaiverSubmission.id?.substring(0, 12)}...</span>
                  </div>
                </div>
              </div>
              
              {/* Waiver/Consent Details */}
              <div style={{ background: '#fff7ed', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#9a3412', fontSize: '1rem' }}>📝 Waiver Acknowledgments</h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {viewingWaiverSubmission.consents?.disclosure && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>IRC §7216 Disclosure Consent</span>
                    </div>
                  )}
                  {viewingWaiverSubmission.consents?.useConsent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Consent to Use Tax Information</span>
                    </div>
                  )}
                  {viewingWaiverSubmission.consents?.incomeWaiver && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px' }}>
                      <span style={{ color: '#f59e0b' }}>⚠️</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Income Documentation Waiver</span>
                    </div>
                  )}
                  {viewingWaiverSubmission.waiverText && (
                    <div style={{ padding: '12px', background: 'white', borderRadius: '8px', marginTop: '8px' }}>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', marginBottom: '6px' }}>Waiver Statement:</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem', fontStyle: 'italic' }}>"{viewingWaiverSubmission.waiverText}"</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Signature */}
              <div style={{ background: '#fefce8', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '1rem' }}>✍️ Electronic Signature</h3>
                {viewingWaiverSubmission.signature?.image && (
                  <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '10px' }}>
                    <img 
                      src={viewingWaiverSubmission.signature.image} 
                      alt="Signature" 
                      style={{ maxWidth: '100%', maxHeight: '100px' }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ color: '#92400e' }}>Typed Name: </span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingWaiverSubmission.signature?.typed}</span>
                  </div>
                  <div>
                    <span style={{ color: '#92400e' }}>IP Address: </span>
                    <span style={{ color: '#64748b' }}>{viewingWaiverSubmission.signature?.ipAddress || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => downloadIRSWaiverPDF(viewingWaiverSubmission, company, preparer)}
                  style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  📥 Download PDF
                </button>
                {viewingWaiverSubmission.status === 'pending_verification' && (
                  <button 
                    onClick={async () => {
                      if (window.confirm('Verify this IRS Waiver submission?')) {
                        await verifyIRSWaiverSubmission(viewingWaiverSubmission.id, preparer.id);
                        setViewingWaiverSubmission(null);
                      }
                    }}
                    style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    ✅ Verify
                  </button>
                )}
                <button 
                  onClick={() => setViewingWaiverSubmission(null)}
                  style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Appointment Details Modal */}
      {expandedAppointmentId && (() => {
        const apt = appointments.find(a => a.id === expandedAppointmentId);
        if (!apt) return null;
        return (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={() => setExpandedAppointmentId(null)}
          >
            <div 
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                padding: '20px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '16px 16px 0 0'
              }}>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1.2rem', fontWeight: '600' }}>
                  📋 Appointment Details
                </h3>
                <button 
                  onClick={() => setExpandedAppointmentId(null)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Status Badge */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                <span style={{
                  padding: '8px 20px',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: apt.status === 'confirmed' ? '#dcfce7' : apt.status === 'completed' ? '#dbeafe' : apt.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                  color: apt.status === 'confirmed' ? '#166534' : apt.status === 'completed' ? '#1e40af' : apt.status === 'cancelled' ? '#991b1b' : '#92400e'
                }}>
                  {apt.status === 'confirmed' ? '✅ Confirmed' : apt.status === 'completed' ? '🎉 Completed' : apt.status === 'cancelled' ? '❌ Cancelled' : '⏳ Pending'}
                </span>
              </div>

              {/* Details Grid */}
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>📅 Date</label>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {new Date(apt.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>🕐 Time</label>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {convert24to12Hour(getFirstTime(apt))}
                    </span>
                  </div>
                  {/* Multi-client time slots detail */}
                  {apt.numberOfClients && parseInt(apt.numberOfClients) > 1 && apt.timeSlots && Array.isArray(apt.timeSlots) && apt.timeSlots.length > 0 && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>👥 {apt.numberOfClients} Client(s) - All Appointments</label>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0' }}>
                        {apt.timeSlots.map((slot, si) => (
                          <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: si < apt.timeSlots.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                            <span style={{ background: '#3b82f6', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 }}>{si + 1}</span>
                            <span style={{ fontWeight: '600', color: '#1e293b' }}>
                              {typeof slot === 'object' 
                                ? `${new Date(slot.date + 'T00:00:00').toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric'})} at ${convert24to12Hour(slot.time)}`
                                : convert24to12Hour(slot)
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>👤 Client Name</label>
                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '1.1rem' }}>{apt.clientName || apt.fullName || 'N/A'}</span>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>📧 Email</label>
                    <span style={{ color: '#1e293b' }}>{apt.email || apt.clientEmail || 'Not provided'}</span>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>📱 Phone</label>
                    <span style={{ color: '#1e293b' }}>{apt.phoneNumber || apt.clientPhone || 'Not provided'}</span>
                  </div>
                  {apt.filingType && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>📄 Filing Type</label>
                      <span style={{ color: '#1e293b' }}>{apt.filingType}</span>
                    </div>
                  )}
                  {apt.notes && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>📝 Notes</label>
                      <span style={{ color: '#1e293b' }}>{apt.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {(() => {
                const status = apt.status || 'pending';
                return (
              <div style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid #e2e8f0', 
                display: 'flex', 
                gap: '12px', 
                flexWrap: 'wrap',
                justifyContent: 'flex-end'
              }}>
                {status === 'pending' && (
                  <button
                    onClick={() => {
                      handleUpdateAppointmentStatus(apt.id, 'confirmed');
                      setExpandedAppointmentId(null);
                    }}
                    style={{
                      padding: '14px 24px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      minHeight: '48px',
                      fontSize: '1rem',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation'
                    }}
                  >
                    ✅ Confirm
                  </button>
                )}
                {(status === 'pending' || status === 'confirmed') && (
                  <button
                    onClick={() => {
                      handleUpdateAppointmentStatus(apt.id, 'completed');
                      setExpandedAppointmentId(null);
                    }}
                    style={{
                      padding: '14px 24px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      minHeight: '48px',
                      fontSize: '1rem',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation'
                    }}
                  >
                    ✓ Complete
                  </button>
                )}
                <button
                  onClick={() => {
                    handleDeleteAppointment(apt.id);
                    setExpandedAppointmentId(null);
                  }}
                  style={{
                    padding: '14px 24px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: '48px',
                    fontSize: '1rem',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                >
                  🗑️ Delete
                </button>
                <button
                  onClick={() => setExpandedAppointmentId(null)}
                  style={{
                    padding: '14px 24px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: '48px',
                    fontSize: '1rem',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                >
                  Close
                </button>
              </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* PDF Document Builder Modal */}
      {showDocumentBuilder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            width: '95vw',
            height: '95vh',
            background: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
          }}>
            <PDFDocumentBuilder
              preparerId={preparer?.id}
              companyId={company?.id}
              existingTemplate={editingTemplate}
              onSave={handleSaveTemplate}
              onClose={() => {
                setShowDocumentBuilder(false);
                setEditingTemplate(null);
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default PreparerDashboard;
